/**
 * DEMO-ONLY (presidio theme override of mock.interceptor.ts).
 * Presidio sessionStorage workflows and response rewrites for local mock ICM.
 * Removable wholesale with the presidio theme / mock-data fixtures.
 * Stock interceptor: mock.interceptor.ts
 *
 * Prefer new DEMO business workflows in `extensions/presidio-demo/mock/*` handlers
 * (e.g. `presidio-mock-ops-handlers.ts`, basket/quotes/punchout modules) and call them
 * from here — do not grow this file with large inline feature implementations.
 */
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { PRIMARY_OUTLET, Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Observable, of, throwError } from 'rxjs';
import { map } from 'rxjs/operators';

import { API_MOCK_PATHS } from 'ish-core/configurations/injection-keys';
import { MfaDeviceTrustService } from 'ish-core/services/mfa-device-trust/mfa-device-trust.service';
import { getRestEndpoint } from 'ish-core/store/core/configuration';
import { InjectSingle } from 'ish-core/utils/injection';

import {
  appendPresidioMockPricingRequest,
  findPresidioMockPricingRequest,
  readPresidioMockPricingRequests,
} from '../../extensions/presidio-demo/mock/presidio-mock-pricing-requests';
import {
  addPresidioMockBasketItems,
  addPresidioMockQuoteToBasket,
  applyPresidioMockBasketLinesToPayload,
  clearPresidioMockBasketLines,
  deleteAllPresidioMockBasketItems,
  deletePresidioMockBasketItem,
  hasPresidioMockBasketLines,
  moneyWithPresidioMockTax,
  readPresidioMockBasketLines,
  separatePresidioMockBasketItem,
  updatePresidioMockBasketItem,
} from '../../extensions/presidio-demo/mock/presidio-mock-basket-state';
import {
  applyPresidioOrderTemplateOverlay,
  mergePresidioCreatedOrderTemplateLinks,
} from '../../extensions/presidio-demo/mock/presidio-mock-order-templates';
import { handlePresidioMockOrderTemplates } from '../../extensions/presidio-demo/mock/presidio-mock-order-templates-handlers';
import {
  clearPresidioMockActiveLogin,
  getPresidioMockActiveCompanyName,
  isPresidioMockWarehouseLogin,
  resolvePresidioMockCustomerRestPath,
  setPresidioMockActiveLogin,
} from '../../extensions/presidio-demo/mock/presidio-mock-session';
import {
  applyActiveBuyerContactToAddress,
  applyDiscoveryPendingApprovalToOrderPayload,
  applyDiscoveryPendingOrdersToList,
  applyDiscoveryPriceToProductPayload,
  applyDiscoveryPricesToProductPricesPayload,
  buildProductFiltersFromProductStubs,
  evaluateDiscoveryPurchaseRuleIntent,
  filterAddressesForDiscoveryVisibility,
  filterMyWarehouseCategoryFromTree,
  filterProductListByActiveCatalog,
  filterProductListElementsByInMyWarehouse,
  getActiveMockBuyerContact,
  handlePresidioPurchaseApprovals,
  isDiscoveryOrderCreatePath,
  isInMyWarehouseFilterSelected,
  classifyPresidioAccessErp,
  isPresidioMockEmailAlreadyRegistered,
  productStubMatchesSearchTerm,
  recordDiscoveryAccessRequest,
  rejectSkuOutsideActiveCatalog,
  sortAddressesRecentlyUsed,
  stampActiveBuyerOntoOrderPayload,
} from '../../extensions/presidio-demo/mock/presidio-mock-discovery-handlers';
import {
  lookupPresidioUnspsc,
  unspscMatchesSearchToken,
} from '../../extensions/presidio-demo/mock/presidio-mock-product-unspsc';
import productsListFixture from '../../../assets/mock-data/products/get.json';
import {
  getCategoryProductsFixture,
} from '../../extensions/presidio-demo/mock/presidio-mock-category-product-lists';
import {
  applyDiscoveryAvailableSoldTos,
  getApprovedEmailDomainsByCustomer,
  getDiscoveryLoginBlockReason,
  isSkuAllowedForActiveCustomer,
} from '../../extensions/presidio-demo/mock/presidio-mock-discovery';
import { isEmailAllowedForApprovedDomains } from '../../extensions/presidio-discovery/utils/presidio-approved-email-domains';
import {
  handlePresidioMockCsrImpersonation,
  handlePresidioMockSalesRepCustomerSwitch,
  rejectPresidioMockPurchaseWhileImpersonating,
} from '../../extensions/presidio-demo/mock/presidio-mock-ops-handlers';
import { handlePresidioMockQuickOrder } from '../../extensions/presidio-demo/mock/presidio-mock-quickorder-handlers';
import { handlePresidioMockProductNotifications } from '../../extensions/presidio-demo/mock/presidio-mock-product-notifications-handlers';
import { resolvePresidioProductSku } from '../../extensions/presidio-demo/mock/presidio-mpn-sku-map';
import {
  applyPresidioMockPortalListOverlay,
  handlePresidioMockPortalMutation,
} from '../../extensions/presidio-demo/mock/presidio-mock-portal-handlers';
import { handlePresidioMockSalesAdmin } from '../../extensions/presidio-demo/mock/presidio-mock-sales-admin-handlers';
import {
  applyAdminAffiliateQuoteListOverlay,
  applyAdminMyAccountAffiliateOverlay,
  buildAdminAffiliateQuoteDetail,
  rewriteAdminMyAccountCustomerPath,
} from '../../extensions/presidio-demo/mock/presidio-mock-admin-my-account';
import {
  applyPresidioQuoteDetailOverlay,
  buildPresidioQuoteDetailFromEnrichment,
  applyPresidioQuoteListVisibility,
  getPresidioQuoteEnrichment,
  isPresidioQuoteCustomerVisible,
  markActivePresidioQuotePurchasedOnCheckout,
  rejectPresidioMockQuote,
} from '../../extensions/presidio-demo/mock/presidio-mock-quotes';
import {
  buildPresidioMockPunchoutUserLink,
  buildPresidioMockPunchoutUsersEnvelope,
  createPresidioMockPunchoutUser,
  deletePresidioMockPunchoutUser,
  getPresidioMockPunchoutUser,
  punchoutTypeForResource,
  updatePresidioMockPunchoutUser,
} from '../../extensions/presidio-demo/mock/presidio-mock-punchout-users';
import { PunchoutType, PunchoutUser } from '../../extensions/punchout/models/punchout-user/punchout-user.model';

const MOCK_DATA_ROOT = './assets/mock-data';

/** CUSTOMIZATION: session key so static mocks can flip QR New → Submitted after POST quotes. */
const MOCK_QR_SUBMITTED_PREFIX = 'presidio-mock-qr-submitted:';

/** CUSTOMIZATION: session key so static mocks can move a cart line into Save for later. */
const MOCK_SFL_SAVED_KEY = 'presidio-mock-sfl-saved';
/** CUSTOMIZATION: JSON snapshot of SFL lines for get-saved rewrite + purchase qty fidelity. */
const MOCK_SFL_LINES_KEY = 'presidio-mock-sfl-lines';

/** CUSTOMIZATION: persist basket invoice/shipping address ids chosen via PATCH. */
const MOCK_BASKET_INVOICE_ADDR_KEY = 'presidio-mock-basket-invoice-addr';
const MOCK_BASKET_SHIPPING_ADDR_KEY = 'presidio-mock-basket-shipping-addr';

/** CUSTOMIZATION: persist deleted payment instruments (demo credit-card removal). */
const MOCK_DELETED_PAYMENT_INSTRUMENTS_KEY = 'presidio-mock-deleted-payment-instruments';

/** CUSTOMIZATION: persist My Account preferred ship-to / payment (set from checkout). */
const MOCK_USER_PREFS_KEY = 'presidio-mock-user-prefs';

/** CUSTOMIZATION: persist open-tender / invoice payment after PUT payments/open-tender. */
const MOCK_BASKET_PAYMENT_KEY = 'presidio-mock-basket-payment';

/** CUSTOMIZATION: persist commonShippingMethod after basket PATCH. */
const MOCK_BASKET_SHIPPING_METHOD_KEY = 'presidio-mock-basket-shipping-method';

/** CUSTOMIZATION: persist basket recurrence (recurring order frequency) after PATCH. */
const MOCK_BASKET_RECURRENCE_KEY = 'presidio-mock-basket-recurrence';

/** CUSTOMIZATION: persist basket custom attributes (desiredDeliveryDate, orderReference, …). */
const MOCK_BASKET_ATTRIBUTES_KEY = 'presidio-mock-basket-attributes';
/** DEMO: stamped confirmation payload so receipt GET survives post-order basket clear. */
const MOCK_LAST_ORDER_KEY = 'presidio-mock-last-order';

/** CUSTOMIZATION: persist customer addresses created during checkout (demo). */
const MOCK_CREATED_ADDRESSES_KEY = 'presidio-mock-created-addresses';

const MOCK_STD_SHIPPING_COST = 249;

const MOCK_SHIPPING_METHOD_CATALOG: Record<
  string,
  {
    id: string;
    name: string;
    description: string;
    deliveryTimeMin: string;
    deliveryTimeMax: string;
    shippingCosts: {
      gross: { value: number; currency: string };
      net: { value: number; currency: string };
    };
  }
> = {
  STD_GROUND: {
    id: 'STD_GROUND',
    name: 'Standard Ground',
    description: '5-7 business days',
    deliveryTimeMin: 'P5D',
    deliveryTimeMax: 'P7D',
    shippingCosts: {
      gross: { value: 249, currency: 'USD' },
      net: { value: 249, currency: 'USD' },
    },
  },
  EXP_2DAY: {
    id: 'EXP_2DAY',
    name: 'Express 2-Day',
    description: '2 business days',
    deliveryTimeMin: 'P2D',
    deliveryTimeMax: 'P2D',
    shippingCosts: {
      gross: { value: 499, currency: 'USD' },
      net: { value: 499, currency: 'USD' },
    },
  },
  OVERNIGHT: {
    id: 'OVERNIGHT',
    name: 'Overnight',
    description: 'Next business day',
    deliveryTimeMin: 'P1D',
    deliveryTimeMax: 'P1D',
    shippingCosts: {
      gross: { value: 799, currency: 'USD' },
      net: { value: 799, currency: 'USD' },
    },
  },
  OWN_ACCOUNT: {
    id: 'OWN_ACCOUNT',
    name: 'Ship on my carrier account',
    description: 'Select carrier and account number — no shipping charge collected',
    deliveryTimeMin: 'P1D',
    deliveryTimeMax: 'P7D',
    shippingCosts: {
      gross: { value: 0, currency: 'USD' },
      net: { value: 0, currency: 'USD' },
    },
  },
  // DEMO MVP 2.6.3 — warehouse-held-only carts (client filters the OPC list to this alone).
  BEST_AVAILABLE: {
    id: 'BEST_AVAILABLE',
    name: 'Best Available',
    description: 'Warehouse held-stock shipment',
    deliveryTimeMin: 'P1D',
    deliveryTimeMax: 'P7D',
    shippingCosts: {
      gross: { value: 0, currency: 'USD' },
      net: { value: 0, currency: 'USD' },
    },
  },
};

const MOCK_STD_SHIPPING_METHOD = MOCK_SHIPPING_METHOD_CATALOG.STD_GROUND;

function mockMoney(value: number): { gross: { value: number; currency: string }; net: { value: number; currency: string } } {
  return {
    gross: { value, currency: 'USD' },
    net: { value, currency: 'USD' },
  };
}

/**
 * DEMO-ONLY: Paytrace-supported checkout methods (Terms, ACH, credit card) — MVP 2.6.4.
 * eCheck / wire omitted (not supported by Paytrace; 2.6.5 list pending workbook update).
 * Paytrace CC remains a stub — production service IDs are PROVISIONAL.
 */
const MOCK_PAYMENT_METHOD_CATALOG: Record<
  string,
  {
    id: string;
    serviceID: string;
    displayName: string;
    description?: string;
    saveAllowed: boolean;
    capabilities: string[];
  }
> = {
  ISH_INVOICE: {
    id: 'ISH_INVOICE',
    serviceID: 'ISH_INVOICE',
    displayName: 'Terms',
    saveAllowed: false,
    capabilities: [],
  },
  ISH_CreditCard: {
    id: 'ISH_CreditCard',
    serviceID: 'ISH_CreditCard',
    displayName: 'Credit Card',
    description: 'Processed via Paytrace (integration pending).',
    saveAllowed: true,
    capabilities: [],
  },
  PRS_ACH: {
    id: 'PRS_ACH',
    serviceID: 'PRS_ACH',
    displayName: 'ACH',
    description: 'Bank transfer via ACH.',
    saveAllowed: false,
    capabilities: [],
  },
};

/** Instrument id → owning payment method id (instrument may differ for saved cards). */
const MOCK_PAYMENT_INSTRUMENT_TO_METHOD: Record<string, string> = {
  ISH_INVOICE: 'ISH_INVOICE',
  PRS_ACH: 'PRS_ACH',
  PRS_CC_SAVED_1111: 'ISH_CreditCard',
  PRS_CC_SAVED_4242: 'ISH_CreditCard',
  PRS_CC_SAVED_5555: 'ISH_CreditCard',
  PRS_CC_SAVED_9999: 'ISH_CreditCard',
  PRS_CC_SAVED_0005: 'ISH_CreditCard',
};

const MOCK_PAYMENT_INSTRUMENT_LABELS: Record<string, string> = {
  ISH_INVOICE: 'Terms',
  PRS_ACH: 'ACH',
  PRS_CC_SAVED_1111: 'Visa ···· 1111',
  PRS_CC_SAVED_4242: 'Mastercard ···· 4242',
  PRS_CC_SAVED_5555: 'Amex ···· 5555',
  PRS_CC_SAVED_9999: 'Visa ···· 9999',
  PRS_CC_SAVED_0005: 'Discover ···· 0005',
};

const MOCK_INVOICE_PAYMENT_METHOD = {
  ...MOCK_PAYMENT_METHOD_CATALOG.ISH_INVOICE,
  paymentInstruments: ['ISH_INVOICE'],
};

const MOCK_OPEN_TENDER_PAYMENT = {
  id: 'open-tender',
  paymentInstrument: 'ISH_INVOICE',
  paymentMethod: 'ISH_INVOICE',
  redirectRequired: false,
  status: 'Unprocessed',
};

/** DEMO-ONLY: shared address catalog for eligible-addresses + basket assign (typeahead testing). */
function buildMockAddress(input: {
  id: string;
  addressName: string;
  companyName1: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  mainDivisionCode: string;
  mainDivisionName: string;
  postalCode: string;
  invoiceToAddress?: boolean;
  shipToAddress?: boolean;
}): { urn: string; address: Record<string, unknown> } {
  const urn = `urn_${input.id.replace(/-/g, '_')}`;
  const invoice = input.invoiceToAddress ?? false;
  const ship = input.shipToAddress ?? true;
  return {
    urn,
    address: {
      id: input.id,
      urn,
      type: 'Address',
      addressName: input.addressName,
      companyName1: input.companyName1,
      firstName: 'Jordan',
      lastName: 'Hale',
      addressLine1: input.addressLine1,
      ...(input.addressLine2 ? { addressLine2: input.addressLine2 } : {}),
      postalCode: input.postalCode,
      city: input.city,
      mainDivision: input.mainDivisionCode,
      mainDivisionCode: input.mainDivisionCode,
      mainDivisionName: input.mainDivisionName,
      country: 'United States',
      countryCode: 'US',
      phoneHome: '555-0100',
      email: 'jordan.hale@acme.com',
      eligibleInvoiceToAddress: invoice,
      eligibleShipToAddress: ship,
      invoiceToAddress: invoice,
      shipToAddress: ship,
      street: input.addressLine1,
    },
  };
}

const MOCK_ADDRESS_BY_ID: Record<string, { urn: string; address: Record<string, unknown> }> = {
  'addr-inv': buildMockAddress({
    id: 'addr-inv',
    addressName: 'Acme HQ — Billing',
    companyName1: 'Acme Healthcare',
    addressLine1: '100 Healthcare Way',
    addressLine2: 'Suite 400',
    city: 'Austin',
    mainDivisionCode: 'TX',
    mainDivisionName: 'Texas',
    postalCode: '78701',
    invoiceToAddress: true,
    shipToAddress: true,
  }),
  'addr-ship': buildMockAddress({
    id: 'addr-ship',
    addressName: 'Acme Data Center — Shipping',
    companyName1: 'Acme Healthcare',
    addressLine1: '500 Server Farm Blvd',
    city: 'Austin',
    mainDivisionCode: 'TX',
    mainDivisionName: 'Texas',
    postalCode: '78758',
  }),
  'addr-ship-dallas': buildMockAddress({
    id: 'addr-ship-dallas',
    addressName: 'Acme Dallas Distribution',
    companyName1: 'Acme Healthcare',
    addressLine1: '2200 Commerce St',
    city: 'Dallas',
    mainDivisionCode: 'TX',
    mainDivisionName: 'Texas',
    postalCode: '75201',
  }),
  'addr-ship-chicago': buildMockAddress({
    id: 'addr-ship-chicago',
    addressName: 'Acme Midwest Hub',
    companyName1: 'Acme Healthcare',
    addressLine1: '233 S Wacker Dr',
    addressLine2: 'Floor 12',
    city: 'Chicago',
    mainDivisionCode: 'IL',
    mainDivisionName: 'Illinois',
    postalCode: '60606',
  }),
  'addr-ship-nyc': buildMockAddress({
    id: 'addr-ship-nyc',
    addressName: 'Acme Northeast Office',
    companyName1: 'Acme Healthcare',
    addressLine1: '1 Penn Plaza',
    city: 'New York',
    mainDivisionCode: 'NY',
    mainDivisionName: 'New York',
    postalCode: '10119',
  }),
  'addr-ship-seattle': buildMockAddress({
    id: 'addr-ship-seattle',
    addressName: 'Acme Pacific NW',
    companyName1: 'Acme Healthcare',
    addressLine1: '1201 3rd Ave',
    city: 'Seattle',
    mainDivisionCode: 'WA',
    mainDivisionName: 'Washington',
    postalCode: '98101',
  }),
  'addr-ship-atlanta': buildMockAddress({
    id: 'addr-ship-atlanta',
    addressName: 'Acme Southeast Depot',
    companyName1: 'Acme Healthcare',
    addressLine1: '191 Peachtree St NE',
    city: 'Atlanta',
    mainDivisionCode: 'GA',
    mainDivisionName: 'Georgia',
    postalCode: '30303',
  }),
  'addr-ship-denver': buildMockAddress({
    id: 'addr-ship-denver',
    addressName: 'Acme Mountain Region',
    companyName1: 'Acme Healthcare',
    addressLine1: '1801 California St',
    city: 'Denver',
    mainDivisionCode: 'CO',
    mainDivisionName: 'Colorado',
    postalCode: '80202',
  }),
  'addr-ship-phoenix': buildMockAddress({
    id: 'addr-ship-phoenix',
    addressName: 'Banner Health Campus',
    companyName1: 'Banner Health',
    addressLine1: '1111 E McDowell Rd',
    city: 'Phoenix',
    mainDivisionCode: 'AZ',
    mainDivisionName: 'Arizona',
    postalCode: '85006',
  }),
  'addr-ship-miami': buildMockAddress({
    id: 'addr-ship-miami',
    addressName: 'Acme LatAm Staging',
    companyName1: 'Acme Healthcare',
    addressLine1: '200 S Biscayne Blvd',
    city: 'Miami',
    mainDivisionCode: 'FL',
    mainDivisionName: 'Florida',
    postalCode: '33131',
  }),
  // DEMO: additional invoice/billing locations (aligned with discovery Manage addresses)
  'addr-inv-2': buildMockAddress({
    id: 'addr-inv-2',
    addressName: 'Billing location 2',
    companyName1: 'Acme Healthcare',
    addressLine1: '2100 Research Blvd',
    city: 'Austin',
    mainDivisionCode: 'TX',
    mainDivisionName: 'Texas',
    postalCode: '78759',
    invoiceToAddress: true,
    shipToAddress: false,
  }),
  'addr-inv-3': buildMockAddress({
    id: 'addr-inv-3',
    addressName: 'Billing location 3',
    companyName1: 'Acme Healthcare',
    addressLine1: '400 Congress Ave',
    addressLine2: 'Floor 8',
    city: 'Austin',
    mainDivisionCode: 'TX',
    mainDivisionName: 'Texas',
    postalCode: '78701',
    invoiceToAddress: true,
    shipToAddress: false,
  }),
  'addr-inv-4': buildMockAddress({
    id: 'addr-inv-4',
    addressName: 'Billing location 4',
    companyName1: 'Acme Healthcare',
    addressLine1: '901 Main St',
    city: 'Dallas',
    mainDivisionCode: 'TX',
    mainDivisionName: 'Texas',
    postalCode: '75202',
    invoiceToAddress: true,
    shipToAddress: false,
  }),
  'addr-inv-5': buildMockAddress({
    id: 'addr-inv-5',
    addressName: 'Billing location 5',
    companyName1: 'Acme Healthcare',
    addressLine1: '1000 Louisiana St',
    city: 'Houston',
    mainDivisionCode: 'TX',
    mainDivisionName: 'Texas',
    postalCode: '77002',
    invoiceToAddress: true,
    shipToAddress: false,
  }),
  'addr-inv-6': buildMockAddress({
    id: 'addr-inv-6',
    addressName: 'Billing location 6',
    companyName1: 'Acme Healthcare',
    addressLine1: '600 W 7th St',
    city: 'Fort Worth',
    mainDivisionCode: 'TX',
    mainDivisionName: 'Texas',
    postalCode: '76102',
    invoiceToAddress: true,
    shipToAddress: false,
  }),
  'addr-inv-7': buildMockAddress({
    id: 'addr-inv-7',
    addressName: 'Billing location 7',
    companyName1: 'Acme Healthcare',
    addressLine1: '300 Convent St',
    city: 'San Antonio',
    mainDivisionCode: 'TX',
    mainDivisionName: 'Texas',
    postalCode: '78205',
    invoiceToAddress: true,
    shipToAddress: false,
  }),
  'addr-inv-8': buildMockAddress({
    id: 'addr-inv-8',
    addressName: 'Billing location 8',
    companyName1: 'Acme Healthcare',
    addressLine1: '201 E Anderson Ln',
    city: 'Austin',
    mainDivisionCode: 'TX',
    mainDivisionName: 'Texas',
    postalCode: '78752',
    invoiceToAddress: true,
    shipToAddress: false,
  }),
};

/**
 * redirects a given request to the REST API to mocked data depending on mock settings
 */
@Injectable()
export class MockInterceptor implements HttpInterceptor {
  private restEndpoint: string;

  constructor(
    @Inject(API_MOCK_PATHS) private apiMockPaths: InjectSingle<typeof API_MOCK_PATHS>,
    store: Store,
    private router: Router
  ) {
    store.pipe(select(getRestEndpoint)).subscribe(data => (this.restEndpoint = data));
  }

  /**
   * Intercepts requests and replaces mock paths with file references.
   */
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.apiMockPaths?.length || !this.urlHasToBeMocked(req.url)) {
      return next.handle(req);
    }

    const restPath = this.getRestPath(req.url);
    this.rememberQuoteSubmit(req, restPath);
    this.rememberSaveForLater(req, restPath);
    this.rememberBasketAddressAssign(req, restPath);
    this.rememberBasketPaymentAssign(req, restPath);
    this.rememberBasketShippingMethodAssign(req, restPath);
    this.rememberBasketRecurrenceAssign(req, restPath);

    // CUSTOMIZATION: MVP 1.2.4 / 1.2.6 — purchase-rule orders on DIAS requisition routes
    const purchaseApprovalsResponse = handlePresidioPurchaseApprovals(req, restPath, b => this.readJsonBody(b));
    if (purchaseApprovalsResponse) {
      // eslint-disable-next-line no-console
      console.log(`mocking purchase approvals '${req.method} ${restPath}'`);
      return purchaseApprovalsResponse;
    }

    // CUSTOMIZATION: DEMO-ONLY ICM quickorder worksheet (custom-quickorder)
    const quickOrderResponse = handlePresidioMockQuickOrder(req, restPath, b => this.readJsonBody(b));
    if (quickOrderResponse) {
      // eslint-disable-next-line no-console
      console.log(`mocking quickorder '${req.method} ${restPath}'`);
      return quickOrderResponse;
    }

    // CUSTOMIZATION: DEMO-ONLY back-in-stock / price alerts for any catalog sku
    const productNotificationsResponse = handlePresidioMockProductNotifications(req, restPath, b =>
      this.readJsonBody(b)
    );
    if (productNotificationsResponse) {
      // eslint-disable-next-line no-console
      console.log(`mocking product notifications '${req.method} ${restPath}'`);
      return productNotificationsResponse;
    }

    // CUSTOMIZATION: multi-ship bucket shipping methods (avoids hung loading on missing fixtures)
    const bucketShippingResponse = this.handleBucketEligibleShippingMethods(req, restPath);
    if (bucketShippingResponse) {
      // eslint-disable-next-line no-console
      console.log(`mocking bucket shipping methods '${req.method} ${restPath}'`);
      return bucketShippingResponse;
    }

    // CUSTOMIZATION: mutable basket line items (add / qty / remove) — sessionStorage demo state
    const basketItemsResponse = this.handleBasketItemMutation(req, restPath);
    if (basketItemsResponse) {
      // eslint-disable-next-line no-console
      console.log(`mocking basket items '${req.method} ${restPath}'`);
      return basketItemsResponse;
    }

    // CUSTOMIZATION: POST baskets/{id}/quotes — add quote lines into mock basket (Add quote to cart)
    const basketQuoteResponse = this.handleBasketQuoteMutation(req, restPath);
    if (basketQuoteResponse) {
      // eslint-disable-next-line no-console
      console.log(`mocking basket quote '${req.method} ${restPath}'`);
      return basketQuoteResponse;
    }

    // CUSTOMIZATION: create/update customer addresses so Add address persists in the picker
    const addressMutationResponse = this.handleAddressMutation(req, restPath);
    if (addressMutationResponse) {
      // eslint-disable-next-line no-console
      console.log(`mocking address mutation '${req.method} ${restPath}'`);
      return addressMutationResponse;
    }

    // CUSTOMIZATION: delete (and create) basket payment instruments so CC delete works in demo
    const paymentInstrumentResponse = this.handlePaymentInstrumentMutation(req, restPath);
    if (paymentInstrumentResponse) {
      // eslint-disable-next-line no-console
      console.log(`mocking payment instrument '${req.method} ${restPath}'`);
      return paymentInstrumentResponse;
    }

    // CUSTOMIZATION: persist preferred ship-to / payment from checkout "Set as default"
    const userPrefsResponse = this.handleUserPreferencesMutation(req, restPath);
    if (userPrefsResponse) {
      // eslint-disable-next-line no-console
      console.log(`mocking user preferences '${req.method} ${restPath}'`);
      return userPrefsResponse;
    }

    // CUSTOMIZATION: basket attributes POST/PATCH/DELETE — echo body + persist (no per-name stub required)
    const attributeResponse = this.handleBasketAttributeMutation(req, restPath);
    if (attributeResponse) {
      // eslint-disable-next-line no-console
      console.log(`mocking basket attribute '${req.method} ${restPath}'`);
      return attributeResponse;
    }

    // CUSTOMIZATION: unauthenticated access-request submit (FR 1.4.5 / 1.2.7 demo)
    const accessRequestVerifyResponse = this.handleAccessRequestVerify(req, restPath);
    if (accessRequestVerifyResponse) {
      return accessRequestVerifyResponse;
    }

    const accessRequestCheckEmailResponse = this.handleAccessRequestCheckEmail(req, restPath);
    if (accessRequestCheckEmailResponse) {
      return accessRequestCheckEmailResponse;
    }

    const accessRequestResponse = this.handleAccessRequest(req, restPath);
    if (accessRequestResponse) {
      // eslint-disable-next-line no-console
      console.log(`mocking access request '${req.method} ${restPath}'`);
      return accessRequestResponse;
    }

    const pricingRequestResponse = this.handlePricingRequest(req, restPath);
    if (pricingRequestResponse) {
      // eslint-disable-next-line no-console
      console.log(`mocking pricing request '${req.method} ${restPath}'`);
      return pricingRequestResponse;
    }

    // CUSTOMIZATION: MVP 8.2.1 — sales-rep affiliate customer switch (extracted demo handler)
    const salesRepSwitchResponse = handlePresidioMockSalesRepCustomerSwitch(req, restPath, b =>
      this.readJsonBody(b)
    );
    if (salesRepSwitchResponse) {
      // eslint-disable-next-line no-console
      console.log(`mocking sales-rep switch '${req.method} ${restPath}'`);
      return salesRepSwitchResponse;
    }

    // CUSTOMIZATION: MVP 8.2.2 — CSR impersonation start/end (extracted demo handler)
    const csrImpersonationResponse = handlePresidioMockCsrImpersonation(req, restPath, b => this.readJsonBody(b));
    if (csrImpersonationResponse) {
      // eslint-disable-next-line no-console
      console.log(`mocking CSR impersonation '${req.method} ${restPath}'`);
      return csrImpersonationResponse;
    }

    // CUSTOMIZATION: MVP 8.2.2 — checkout/order blocked while co-browsing (ATC still allowed)
    const impersonationPurchaseBlock = rejectPresidioMockPurchaseWhileImpersonating(req, restPath);
    if (impersonationPurchaseBlock) {
      return impersonationPurchaseBlock;
    }

    // CUSTOMIZATION: discovery purchase rules — evaluate before cart clear; stamp after POST overlay
    const discoveryPurchaseRule = evaluateDiscoveryPurchaseRuleIntent(req.method, restPath, () =>
      readPresidioMockBasketLines().map(l => ({
        sku: l.sku,
        quantity: l.quantity,
        unitPrice: l.unitPrice,
        quoteId: l.quoteId,
      }))
    );

    // CUSTOMIZATION: MVP 6.x — portal device labels + work-item create/delete (session demo)
    const portalMutation = handlePresidioMockPortalMutation(req, restPath, b => this.readJsonBody(b));
    if (portalMutation) {
      // eslint-disable-next-line no-console
      console.log(`mocking portal mutation '${req.method} ${restPath}'`);
      return portalMutation;
    }

    // CUSTOMIZATION: Sales Admin — price lists / catalogs / work-item respond
    const salesAdmin = handlePresidioMockSalesAdmin(req, restPath, b => this.readJsonBody(b));
    if (salesAdmin) {
      // eslint-disable-next-line no-console
      console.log(`mocking sales-admin '${req.method} ${restPath}'`);
      return salesAdmin;
    }

    // CUSTOMIZATION: MVP 2.4.1 / 2.4.5 / FR8.2.2 — order-template create/share/optional + account-visible carts
    const orderTemplateMutation = handlePresidioMockOrderTemplates(req, restPath, b => this.readJsonBody(b));
    if (orderTemplateMutation) {
      // eslint-disable-next-line no-console
      console.log(`mocking order-template mutation '${req.method} ${restPath}'`);
      return orderTemplateMutation;
    }

    // CUSTOMIZATION: MVP 1.3.1 / 2.5.x — My Account punchout users CRUD + cXML config stub
    const punchoutAdminResponse = this.handlePunchoutAdmin(req, restPath);
    if (punchoutAdminResponse) {
      // eslint-disable-next-line no-console
      console.log(`mocking punchout admin '${req.method} ${restPath}'`);
      return punchoutAdminResponse;
    }

    // CUSTOMIZATION: MVP 3.1.5 / 3.2.5 — quote reject overlay + hide non-customer-visible detail
    const quoteDemoResponse = this.handlePresidioQuoteDemo(req, restPath);
    if (quoteDemoResponse) {
      // eslint-disable-next-line no-console
      console.log(`mocking quote demo '${req.method} ${restPath}'`);
      return quoteDemoResponse;
    }

    // CUSTOMIZATION: Shop catalog root — aggregated products + facets (no on-disk SHOP fixtures)
    const shopCatalogResponse = this.handleShopCatalogMocks(req, restPath);
    if (shopCatalogResponse) {
      // eslint-disable-next-line no-console
      console.log(`mocking Shop catalog '${req.method} ${restPath}'`);
      return shopCatalogResponse;
    }

    // CUSTOMIZATION: FR 1.4.7 — 2SV preference + MFA-gated token
    const twoStepResponse = this.handleTwoStepVerification(req, restPath);
    if (twoStepResponse) {
      // eslint-disable-next-line no-console
      console.log(`mocking two-step verification '${req.method} ${restPath}'`);
      return twoStepResponse;
    }
    const tokenMfaResponse = this.handleTokenMfa(req, restPath);
    if (tokenMfaResponse) {
      // eslint-disable-next-line no-console
      console.log(`mocking token MFA '${req.method} ${restPath}'`);
      return tokenMfaResponse;
    }
    const mfaResendResponse = this.handleMfaResend(req, restPath);
    if (mfaResendResponse) {
      // eslint-disable-next-line no-console
      console.log(`mocking mfa-resend '${req.method} ${restPath}'`);
      return mfaResendResponse;
    }
    const tokenLogoutResponse = this.handleTokenLogout(req, restPath);
    if (tokenLogoutResponse) {
      // eslint-disable-next-line no-console
      console.log(`mocking token logout '${req.method} ${restPath}'`);
      return tokenLogoutResponse;
    }

    let methodFile = `${req.method.toLocaleLowerCase()}.json`;
    // CUSTOMIZATION: after mock submit, serve get-submitted.json for MOCK-QR-NEW detail
    if (this.shouldServeSubmittedQuoteRequest(restPath, req.method)) {
      methodFile = 'get-submitted.json';
    }
    // CUSTOMIZATION: after Save for later POST, serve populated SFL list (cart stays mutable)
    if (this.shouldServeSavedSaveForLater(restPath, req.method)) {
      methodFile = 'get-saved.json';
    }

    // DEMO: warehouse login rewrites customers/- (+ users/-) to PRESIDIO-WH-MOCK fixtures
    // DEMO: admin My Account portal lists stay on home customer across affiliates (8.2.1)
    const resolvedRestPath = resolvePresidioMockCustomerRestPath(restPath);
    const adminRestPath = rewriteAdminMyAccountCustomerPath(resolvedRestPath) || resolvedRestPath;
    // DEMO MVP 2.1.6 / Quick Order: resolve manufacturer part # → ICM sku for product mocks
    const assetRestPath = this.resolveProductPathByManufacturerPart(adminRestPath);

    // Normalize %40 → @ so createUrlTree hits the on-disk mock folders
    const mockUrl = decodeURIComponent(`${MOCK_DATA_ROOT}/${assetRestPath}/${methodFile}`);
    const commands = this.router.parseUrl(mockUrl)?.root?.children?.[PRIMARY_OUTLET]?.segments?.map(s => s.path);
    const newUrl = this.router.createUrlTree(commands)?.toString();

    // eslint-disable-next-line no-console
    console.log(`redirecting '${req.url}' to '${newUrl}'`);

    return next.handle(req.clone({ url: newUrl, method: 'GET' })).pipe(
      map(event => {
        if (!(event instanceof HttpResponse)) {
          return event;
        }
        let body = event.body;
        body = this.applyProductFilterSelection(req, restPath, body);
        body = this.applyProductListFilter(req, restPath, body);
        // Catalog SKU filter before pagination so `total` and page slices stay consistent.
        body = this.applyDiscoveryCatalogAndPricing(restPath, req.method, body);
        // DEMO-ONLY: honor amount/offset so PLP endless-scroll matches DIAS paging behavior
        body = this.applyProductListPagination(req, restPath, body);
        body = this.applyCustomSuggestFilter(req, restPath, body, typeof event.url === 'string' ? event.url : '');
        body = this.applyDiscoveryUserAffiliates(restPath, req.method, body);
        body = this.applyMockAddressCatalog(restPath, req.method, body);
        body = this.applyDeletedPaymentInstruments(restPath, req.method, body);
        body = this.applyUserPreferenceOverrides(restPath, req.method, body);
        body = this.applyBasketSessionOverrides(restPath, req.method, body);
        if (discoveryPurchaseRule && isDiscoveryOrderCreatePath(req.method, restPath)) {
          // eslint-disable-next-line no-console
          console.log(`mocking discovery purchase-rule pending approval '${req.method} ${restPath}'`);
          body = applyDiscoveryPendingApprovalToOrderPayload(body, discoveryPurchaseRule);
          this.persistLastOrderSnapshot(body);
        }
        body = applyDiscoveryPendingOrdersToList(restPath, req.method, body);
        body = applyAdminMyAccountAffiliateOverlay(restPath, req.method, body);
        body = this.applySaveForLaterSessionOverrides(restPath, req.method, body);
        body = this.applyOrderTemplateSessionOverrides(restPath, req.method, body);
        body = this.applyQuoteVisibilityAndEnrichment(restPath, req.method, body);
        body = applyPresidioMockPortalListOverlay(restPath, req.method, body);
        return body === event.body ? event : event.clone({ body });
      })
    );
  }

  /**
   * DEMO: Shop (`SHOP`) has no on-disk products/productfilters fixtures. Serve an
   * aggregate of shopping-department catalogs so the Shop CLP facet rail and
   * refinement PLP work like other category pages.
   */
  private handleShopCatalogMocks(
    req: HttpRequest<unknown>,
    restPath: string
  ): Observable<HttpEvent<unknown>> | null {
    if (req.method.toUpperCase() !== 'GET') {
      return null;
    }
    const path = (restPath || '').replace(/;[^/]*/g, '');
    const shopProducts = getCategoryProductsFixture('SHOP');
    if (!shopProducts) {
      return null;
    }

    if (path === 'categories/SHOP/productfilters') {
      const body = buildProductFiltersFromProductStubs(shopProducts, {
        searchTerm: (
          this.readRequestParam(req, 'searchTerm') ||
          this.readRequestParam(req, 'SearchTerm') ||
          ''
        ).trim(),
        selected: this.parseFilterQueryParams(req),
        filterBasePath: 'categories/SHOP/productfilters',
        skipCatalogFilter: true,
      });
      return of(new HttpResponse({ status: 200, body, url: req.url }));
    }

    if (path === 'categories/SHOP/products') {
      return of(new HttpResponse({ status: 200, body: shopProducts, url: req.url })).pipe(
        map(event => {
          if (!(event instanceof HttpResponse)) {
            return event;
          }
          let body = event.body;
          body = this.applyProductListFilter(req, restPath, body);
          body = this.applyDiscoveryCatalogAndPricing(restPath, req.method, body);
          body = this.applyProductListPagination(req, restPath, body);
          return body === event.body ? event : event.clone({ body });
        })
      );
    }

    return null;
  }

  /**
   * DEMO-ONLY MVP 3.1.5 / 3.2.5: 404 hidden quotes; persist reject overlay on PUT.
   */
  private handlePresidioQuoteDemo(
    req: HttpRequest<unknown>,
    restPath: string
  ): Observable<HttpEvent<unknown>> | null {
    const detail = /^customers\/[^/]+\/users\/[^/]+\/quotes\/([^/]+)$/.exec(restPath);
    if (!detail) {
      return null;
    }
    const quoteId = decodeURIComponent(detail[1]);
    const method = req.method.toUpperCase();
    const affiliateDetail = method === 'GET' ? buildAdminAffiliateQuoteDetail(quoteId) : null;
    if (affiliateDetail) {
      return of(new HttpResponse({ status: 200, body: affiliateDetail, url: req.url }));
    }
    const enrichment = getPresidioQuoteEnrichment(quoteId);

    if (method === 'GET' && enrichment && !isPresidioQuoteCustomerVisible(enrichment)) {
      return throwError(
        () =>
          new HttpErrorResponse({
            status: 404,
            error: { error: `Quote ${quoteId} is not available to this customer` },
            url: req.url,
          })
      );
    }

    // DEMO: serve visible catalog quotes from enrichment so new mock rows do not need
    // per-id static get.json files (list is already rebuilt from the catalog).
    if (method === 'GET' && enrichment && isPresidioQuoteCustomerVisible(enrichment)) {
      const body = applyPresidioQuoteDetailOverlay(
        quoteId,
        buildPresidioQuoteDetailFromEnrichment(enrichment)
      );
      return of(new HttpResponse({ status: 200, body, url: req.url }));
    }

    if (method === 'PUT' && enrichment) {
      const payload = this.readJsonBody<{
        rejected?: boolean;
        rejectionComment?: string;
        rejectionAttachmentName?: string;
      }>(req.body);
      if (payload?.rejected) {
        const updated = rejectPresidioMockQuote(
          quoteId,
          payload.rejectionComment,
          payload.rejectionAttachmentName
        );
        if (updated) {
          const body = applyPresidioQuoteDetailOverlay(
            quoteId,
            buildPresidioQuoteDetailFromEnrichment(updated)
          );
          return of(new HttpResponse({ status: 200, body, url: req.url }));
        }
      }
      // Fall through to static put.json via normal mock path
      return null;
    }

    return null;
  }

  /**
   * DEMO-ONLY MVP 3.1.5 / 3.2.x: rebuild visible quote list from catalog; enrich detail.
   */
  private applyQuoteVisibilityAndEnrichment(restPath: string, method: string, body: unknown): unknown {
    if (method.toUpperCase() !== 'GET') {
      return body;
    }
    // Presidio inbound model: no on-site quote requests — keep list empty.
    if (/(^|\/)quoterequests$/.test(restPath)) {
      return { elements: [] };
    }
    if (/(^|\/)quotes$/.test(restPath)) {
      if (body && typeof body === 'object' && 'elements' in (body as object)) {
        return applyAdminAffiliateQuoteListOverlay(applyPresidioQuoteListVisibility(body));
      }
    }
    const detail = /^customers\/[^/]+\/users\/[^/]+\/quotes\/([^/]+)$/.exec(restPath);
    if (detail) {
      const quoteId = decodeURIComponent(detail[1]);
      const affiliateDetail = buildAdminAffiliateQuoteDetail(quoteId);
      if (affiliateDetail) {
        return affiliateDetail;
      }
      const overlaid = applyPresidioQuoteDetailOverlay(quoteId, body);
      return overlaid === null ? body : overlaid;
    }
    return body;
  }

  /** DEMO-ONLY: merge share/optional overlays + session-created carts onto wishlist GET. */
  private applyOrderTemplateSessionOverrides(restPath: string, method: string, body: unknown): unknown {
    if (method.toUpperCase() !== 'GET') {
      return body;
    }
    if (/(?:customers\/[^/]+\/users\/[^/]+\/wishlists)$/.test(restPath)) {
      return mergePresidioCreatedOrderTemplateLinks(body);
    }
    const match = /(?:customers\/[^/]+\/users\/[^/]+\/wishlists)\/([^/]+)$/.exec(restPath);
    if (!match) {
      return body;
    }
    return applyPresidioOrderTemplateOverlay(match[1], body);
  }

  /**
   * DEMO-ONLY: My Account Punchout — list/create/update/delete users + stub cXML configuration.
   * Types/OCI config continue to use static mock-data fixtures.
   */
  private handlePunchoutAdmin(req: HttpRequest<unknown>, restPath: string): Observable<HttpEvent<unknown>> | null {
    const configMatch =
      /^customers\/([^/]+)\/punchouts\/cxml1\.2\/users\/([^/]+)\/configurations$/.exec(restPath);
    if (configMatch) {
      const method = req.method.toUpperCase();
      const stub = {
        data: [
          { name: 'Extrinsic.UserEmail', value: '' },
          { name: 'ItemIn.quantity', value: '' },
        ],
        info: {
          metaData: [
            {
              name: 'Extrinsic.UserEmail',
              defaultValue: '',
              description: 'Buyer user email extrinsic (demo stub).',
              inputType: 'text',
            },
            {
              name: 'ItemIn.quantity',
              defaultValue: '',
              description: 'Quantity mapping (demo stub).',
              inputType: 'text',
            },
          ],
        },
      };
      if (method === 'GET') {
        return of(new HttpResponse({ status: 200, body: stub, url: req.url }));
      }
      if (method === 'PATCH') {
        const body = this.readJsonBody<unknown[]>(req.body);
        return of(
          new HttpResponse({
            status: 200,
            body: {
              ...stub,
              data: Array.isArray(body) ? body : stub.data,
            },
            url: req.url,
          })
        );
      }
      return null;
    }

    const usersCollection = /^customers\/([^/]+)\/punchouts\/(cxml1\.2|oci5)\/users$/.exec(restPath);
    if (usersCollection) {
      const customerNo = usersCollection[1];
      const resource = usersCollection[2];
      const type = punchoutTypeForResource(resource);
      if (!type) {
        return null;
      }
      const method = req.method.toUpperCase();
      if (method === 'GET') {
        return of(
          new HttpResponse({
            status: 200,
            body: buildPresidioMockPunchoutUsersEnvelope(customerNo, type),
            url: req.url,
          })
        );
      }
      if (method === 'POST') {
        const payload = this.readJsonBody<Partial<PunchoutUser> & { login?: string; punchoutType?: PunchoutType }>(
          req.body
        );
        const login = (payload?.login || '').trim();
        if (!login) {
          return throwError(
            () => new HttpErrorResponse({ status: 400, error: { error: 'login required' }, url: req.url })
          );
        }
        try {
          const created = createPresidioMockPunchoutUser({
            login,
            email: payload?.email,
            active: payload?.active !== false,
            punchoutType: type,
          });
          return of(
            new HttpResponse({
              status: 201,
              body: buildPresidioMockPunchoutUserLink(customerNo, type, created.login || created.id),
              url: req.url,
            })
          );
        } catch {
          return throwError(
            () =>
              new HttpErrorResponse({
                status: 409,
                error: { error: 'Punchout user already exists' },
                url: req.url,
              })
          );
        }
      }
      return null;
    }

    const userItem = /^customers\/([^/]+)\/punchouts\/(cxml1\.2|oci5)\/users\/([^/]+)$/.exec(restPath);
    if (userItem) {
      const resource = userItem[2];
      const login = decodeURIComponent(userItem[3]);
      const type = punchoutTypeForResource(resource);
      if (!type) {
        return null;
      }
      const method = req.method.toUpperCase();
      if (method === 'GET') {
        const user = getPresidioMockPunchoutUser(type, login);
        if (!user) {
          return throwError(
            () => new HttpErrorResponse({ status: 404, error: { error: 'Punchout user not found' }, url: req.url })
          );
        }
        return of(new HttpResponse({ status: 200, body: { ...user, password: undefined }, url: req.url }));
      }
      if (method === 'PUT') {
        const payload = this.readJsonBody<PunchoutUser>(req.body);
        try {
          const updated = updatePresidioMockPunchoutUser({
            id: payload?.id || login,
            login,
            email: payload?.email,
            active: payload?.active !== false,
            punchoutType: type,
            password: payload?.password,
          });
          return of(new HttpResponse({ status: 200, body: updated, url: req.url }));
        } catch {
          return throwError(
            () => new HttpErrorResponse({ status: 404, error: { error: 'Punchout user not found' }, url: req.url })
          );
        }
      }
      if (method === 'DELETE') {
        try {
          deletePresidioMockPunchoutUser(type, login);
          return of(new HttpResponse({ status: 204, body: null, url: req.url }));
        } catch {
          return throwError(
            () => new HttpErrorResponse({ status: 404, error: { error: 'Punchout user not found' }, url: req.url })
          );
        }
      }
      return null;
    }

    return null;
  }

  /**
   * DEMO-ONLY: account-specific catalog SKU filter + negotiated sale prices.
   */
  private applyDiscoveryCatalogAndPricing(restPath: string, method: string, body: unknown): unknown {
    if (method.toUpperCase() !== 'GET') {
      return body;
    }
    // My Warehouse (WH) first-nav category — warehouse login only
    if (restPath === 'categories' || restPath.startsWith('categories?')) {
      return filterMyWarehouseCategoryFromTree(body);
    }
    // Hide My Warehouse PLP products unless warehouse login
    if (/^categories\/WH(?:\/|;|$)/.test(restPath) && !isPresidioMockWarehouseLogin()) {
      return { elements: [], total: 0, offset: 1, amount: 0, type: 'ResourceCollection' };
    }
    if (this.isProductsListRestPath(restPath) || /categories\/[^/]+\/products(?:;|$)/.test(restPath)) {
      return filterProductListByActiveCatalog(body);
    }
    const productMatch = /^products\/([^/]+)$/.exec(restPath);
    if (productMatch) {
      const entered = decodeURIComponent(productMatch[1]);
      const sku = resolvePresidioProductSku(entered);
      if (!isSkuAllowedForActiveCustomer(sku)) {
        return {
          ...(typeof body === 'object' && body ? body : {}),
          sku,
          catalogRestricted: true,
          purchasable: false,
          failed: true,
        };
      }
      return applyDiscoveryPriceToProductPayload(body, sku);
    }
    if (/productprices/.test(restPath) && body && typeof body === 'object') {
      // MVP 2.6.3 — warehouse held SKUs must show $0.00 on PLP/PDP (SalePrice gross/net)
      return applyDiscoveryPricesToProductPricesPayload(body);
    }
    return body;
  }

  /** DEMO: keep availableSoldTos aligned with discovery affiliate assignments. */
  private applyDiscoveryUserAffiliates(restPath: string, method: string, body: unknown): unknown {
    if (method.toUpperCase() !== 'GET') {
      return body;
    }
    if (!/customers\/[^/]+\/users\/[^/]+$/.test(restPath)) {
      return body;
    }
    return applyDiscoveryAvailableSoldTos(body);
  }

  /**
   * DEMO-ONLY: serve the expanded address catalog for checkout typeahead testing
   * (eligible-addresses + customer address book list/detail), including session-created addresses.
   */
  private applyMockAddressCatalog(restPath: string, method: string, body: unknown): unknown {
    if (method.toUpperCase() !== 'GET') {
      return body;
    }

    const created = this.readCreatedAddresses();

    if (/baskets\/[^/]+\/eligible-addresses$/.test(restPath)) {
      const staticAddresses = Object.values(MOCK_ADDRESS_BY_ID).map(entry => ({ ...entry.address }));
      const visible = sortAddressesRecentlyUsed(
        filterAddressesForDiscoveryVisibility([...staticAddresses, ...created] as Record<string, unknown>[])
      );
      return {
        data: visible,
      };
    }

    const addressDetail = /customers\/[^/]+\/addresses\/([^/]+)$/.exec(restPath);
    if (addressDetail) {
      const id = addressDetail[1];
      const createdMatch = created.find(a => a.id === id);
      if (createdMatch) {
        return { ...createdMatch };
      }
      const entry = MOCK_ADDRESS_BY_ID[id];
      return entry ? { ...entry.address } : body;
    }

    if (/customers\/[^/]+\/addresses$/.test(restPath)) {
      const customerMatch = /customers\/([^/]+)\/addresses$/.exec(restPath);
      const customerKey = customerMatch?.[1] || '-';
      const staticIds = Object.keys(MOCK_ADDRESS_BY_ID);
      const createdIds = created.map(a => String(a.id));
      return {
        elements: [...staticIds, ...createdIds].map(id => ({
          type: 'Link',
          uri: `inSPIRED-inTRONICS_Business-Site/-/customers/${customerKey}/addresses/${id}`,
          title: id,
          itemId: id,
        })),
      };
    }

    return body;
  }

  /** DEMO-ONLY: DELETE basket payment instruments so saved credit cards can be removed. */
  private handlePaymentInstrumentMutation(
    req: HttpRequest<unknown>,
    restPath: string
  ): Observable<HttpEvent<unknown>> | null {
    const match = /baskets\/(?:current|MOCK-BASKET-001)\/payment-instruments(?:\/([^/]+))?$/.exec(restPath);
    if (!match) {
      return null;
    }
    const method = req.method.toUpperCase();
    const instrumentId = match[1];

    if (method === 'DELETE' && instrumentId) {
      this.rememberDeletedPaymentInstrument(instrumentId);
      if (typeof sessionStorage !== 'undefined') {
        const current = sessionStorage.getItem(MOCK_BASKET_PAYMENT_KEY);
        if (current === instrumentId) {
          sessionStorage.setItem(MOCK_BASKET_PAYMENT_KEY, '');
        }
      }
      return of(new HttpResponse({ status: 204, body: null }));
    }

    if (method === 'POST') {
      const payload = this.readJsonBody<{
        paymentMethod?: string;
        parameters?: { name: string; value: string }[];
      }>(req.body);
      const id = `PRS_CC_NEW_${Date.now()}`;
      const last4 =
        payload?.parameters?.find(p => p.name === 'creditCardNumber')?.value?.replace(/\D/g, '').slice(-4) || '0000';
      const instrument = {
        id,
        accountIdentifier: `Card ···· ${last4}`,
        parameters: [
          { name: 'cardBrand', value: 'Card' },
          { name: 'cardLast4', value: last4 },
          ...(payload?.parameters || []).filter(p =>
            ['cardholderName', 'billingName', 'billingAddressLine1', 'billingCity', 'billingState', 'billingPostalCode'].includes(
              p.name
            )
          ),
        ],
      };
      // Persist as selectable by clearing any prior delete of this id and storing in session created list.
      this.rememberCreatedPaymentInstrument(instrument, payload?.paymentMethod || 'ISH_CreditCard');
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(MOCK_BASKET_PAYMENT_KEY, id);
      }
      return of(new HttpResponse({ status: 201, body: { data: instrument } }));
    }

    return null;
  }

  private readDeletedPaymentInstruments(): string[] {
    if (typeof sessionStorage === 'undefined') {
      return [];
    }
    try {
      const raw = sessionStorage.getItem(MOCK_DELETED_PAYMENT_INSTRUMENTS_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  }

  private rememberDeletedPaymentInstrument(id: string): void {
    if (typeof sessionStorage === 'undefined' || !id) {
      return;
    }
    const next = Array.from(new Set([...this.readDeletedPaymentInstruments(), id]));
    sessionStorage.setItem(MOCK_DELETED_PAYMENT_INSTRUMENTS_KEY, JSON.stringify(next));
  }

  private rememberCreatedPaymentInstrument(
    instrument: { id: string; accountIdentifier?: string; parameters?: { name: string; value: string }[] },
    paymentMethodId: string
  ): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    const key = 'presidio-mock-created-payment-instruments';
    try {
      const raw = sessionStorage.getItem(key);
      const list = raw ? JSON.parse(raw) : [];
      const next = Array.isArray(list) ? list.filter((i: { id?: string }) => i?.id !== instrument.id) : [];
      next.push({ ...instrument, paymentMethodId });
      sessionStorage.setItem(key, JSON.stringify(next));
    } catch {
      sessionStorage.setItem(key, JSON.stringify([{ ...instrument, paymentMethodId }]));
    }
  }

  private readCreatedPaymentInstruments(): {
    id: string;
    accountIdentifier?: string;
    parameters?: { name: string; value: string }[];
    paymentMethodId?: string;
  }[] {
    if (typeof sessionStorage === 'undefined') {
      return [];
    }
    try {
      const raw = sessionStorage.getItem('presidio-mock-created-payment-instruments');
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private readUserPrefs(): {
    preferredShipToAddressUrn?: string;
    preferredInvoiceToAddressUrn?: string;
    preferredPaymentInstrumentId?: string;
  } {
    if (typeof sessionStorage === 'undefined') {
      return {};
    }
    try {
      const raw = sessionStorage.getItem(MOCK_USER_PREFS_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private writeUserPrefs(prefs: {
    preferredShipToAddressUrn?: string;
    preferredInvoiceToAddressUrn?: string;
    preferredPaymentInstrumentId?: string;
  }): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    try {
      sessionStorage.setItem(MOCK_USER_PREFS_KEY, JSON.stringify(prefs));
    } catch {
      /* ignore quota */
    }
  }

  /**
   * DEMO-ONLY: PUT customers/-/users/- — persist preferred ship-to / payment from checkout.
   */
  private handleUserPreferencesMutation(
    req: HttpRequest<unknown>,
    restPath: string
  ): Observable<HttpEvent<unknown>> | null {
    if (req.method.toUpperCase() !== 'PUT' || !/^customers\/[^/]+\/users\/[^/]+$/.test(restPath)) {
      return null;
    }
    const body = this.readJsonBody<{
      preferredShipToAddress?: { urn?: string };
      preferredInvoiceToAddress?: { urn?: string };
      preferredPaymentInstrument?: { id?: string };
      firstName?: string;
      lastName?: string;
      email?: string;
      login?: string;
      preferredLanguage?: string;
      phoneHome?: string;
      title?: string;
    }>(req.body);

    const prefs = {
      preferredShipToAddressUrn: body?.preferredShipToAddress?.urn,
      preferredInvoiceToAddressUrn: body?.preferredInvoiceToAddress?.urn,
      preferredPaymentInstrumentId: body?.preferredPaymentInstrument?.id,
    };
    this.writeUserPrefs(prefs);

    return of(
      new HttpResponse({
        status: 200,
        body: {
          firstName: body?.firstName || 'Jordan',
          lastName: body?.lastName || 'Hale',
          email: body?.email || 'jordan.hale@acme.com',
          login: body?.login || 'jordan.hale@acme.com',
          preferredLanguage: body?.preferredLanguage || 'en_US',
          phoneHome: body?.phoneHome || '555-0100',
          title: body?.title || '',
          preferredShipToAddress: prefs.preferredShipToAddressUrn
            ? { urn: prefs.preferredShipToAddressUrn }
            : undefined,
          preferredInvoiceToAddress: prefs.preferredInvoiceToAddressUrn
            ? { urn: prefs.preferredInvoiceToAddressUrn }
            : undefined,
          preferredPaymentInstrument: prefs.preferredPaymentInstrumentId
            ? { id: prefs.preferredPaymentInstrumentId }
            : {},
        },
        url: req.url,
      })
    );
  }

  /** DEMO-ONLY: merge session preferred fields onto GET user payloads. */
  private applyUserPreferenceOverrides(restPath: string, method: string, body: unknown): unknown {
    if (method.toUpperCase() !== 'GET' || !/^customers\/[^/]+\/users\/[^/]+$/.test(restPath)) {
      return body;
    }
    if (!body || typeof body !== 'object') {
      return body;
    }
    // Only override once the shopper has saved prefs in this session. Until then,
    // fixture preferred* values remain (demo "with defaults"). An empty saved prefs
    // object clears fixture defaults so checkout starts with no preselection.
    if (typeof sessionStorage === 'undefined' || sessionStorage.getItem(MOCK_USER_PREFS_KEY) === null) {
      return body;
    }
    const prefs = this.readUserPrefs();
    const user = { ...(body as Record<string, unknown>) };
    if (prefs.preferredShipToAddressUrn) {
      user.preferredShipToAddress = { urn: prefs.preferredShipToAddressUrn };
    } else {
      delete user.preferredShipToAddress;
    }
    if (prefs.preferredInvoiceToAddressUrn) {
      user.preferredInvoiceToAddress = { urn: prefs.preferredInvoiceToAddressUrn };
    } else {
      delete user.preferredInvoiceToAddress;
    }
    if (prefs.preferredPaymentInstrumentId) {
      user.preferredPaymentInstrument = { id: prefs.preferredPaymentInstrumentId };
    } else {
      delete user.preferredPaymentInstrument;
    }
    return user;
  }

  /** DEMO-ONLY: remove deleted instruments (and append created ones) from eligible payment methods. */
  private applyDeletedPaymentInstruments(restPath: string, method: string, body: unknown): unknown {
    if (method.toUpperCase() !== 'GET' || !/eligible-payment-methods$/.test(restPath)) {
      return body;
    }
    if (!body || typeof body !== 'object') {
      return body;
    }
    const deleted = new Set(this.readDeletedPaymentInstruments());
    const created = this.readCreatedPaymentInstruments().filter(i => i?.id && !deleted.has(i.id));
    const payload = body as {
      data?: Array<{ id?: string; paymentInstruments?: string[]; description?: string }>;
      included?: { paymentInstruments?: Record<string, unknown> };
    };
    if (!payload.data) {
      return body;
    }

    const included = { ...(payload.included?.paymentInstruments || {}) };
    for (const id of deleted) {
      delete included[id];
    }
    for (const instrument of created) {
      included[instrument.id] = {
        id: instrument.id,
        accountIdentifier: instrument.accountIdentifier,
        parameters: instrument.parameters || [],
      };
    }

    const data = payload.data
      .filter(methodRow => methodRow.id !== 'PRS_ECHECK' && methodRow.id !== 'PRS_WIRE')
      .map(methodRow => {
        let instruments = (methodRow.paymentInstruments || []).filter(id => !deleted.has(id));
        if (methodRow.id === 'ISH_CreditCard' || methodRow.id === 'ISH_INVOICE') {
          const extras = created
            .filter(i => (i.paymentMethodId || 'ISH_CreditCard') === methodRow.id)
            .map(i => i.id)
            .filter(id => !instruments.includes(id));
          instruments = [...instruments, ...extras];
        }
        // Terms: no customer-specific terms label in MVP (term length unknown at checkout).
        if (methodRow.id === 'ISH_INVOICE') {
          const { description: _omit, ...rest } = methodRow;
          return { ...rest, paymentInstruments: instruments };
        }
        return { ...methodRow, paymentInstruments: instruments };
      });

    return {
      ...payload,
      data,
      included: { ...(payload.included || {}), paymentInstruments: included },
    };
  }

  /** DEMO-ONLY: POST/PUT customer addresses into sessionStorage so new ship-tos appear in the picker. */
  private handleAddressMutation(req: HttpRequest<unknown>, restPath: string): Observable<HttpEvent<unknown>> | null {
    const method = req.method.toUpperCase();
    const createMatch = /customers\/([^/]+)\/addresses$/.exec(restPath);
    const updateMatch = /customers\/([^/]+)\/addresses\/([^/]+)$/.exec(restPath);

    // GET customers/.../addresses/{id} — catalog extras (addr-inv-2+) and session-created
    // addresses have no on-disk get.json; serve from memory or the request 404s first.
    if (method === 'GET' && updateMatch) {
      const id = decodeURIComponent(updateMatch[2]);
      const createdMatch = this.readCreatedAddresses().find(a => a.id === id);
      if (createdMatch) {
        return of(new HttpResponse({ status: 200, body: { ...createdMatch }, url: req.url }));
      }
      const staticEntry = MOCK_ADDRESS_BY_ID[id];
      if (staticEntry) {
        return of(new HttpResponse({ status: 200, body: { ...staticEntry.address }, url: req.url }));
      }
    }

    if (method === 'POST' && createMatch) {
      const customerKey = createMatch[1] || 'PRESIDIO-MOCK';
      const payload = this.readJsonBody<Record<string, unknown>>(req.body) || {};
      const id = `addr-user-${Date.now()}`;
      const urn = `urn_${id.replace(/-/g, '_')}`;
      const wantsInvoice = payload['invoiceToAddress'] === true || payload['eligibleInvoiceToAddress'] === true;
      const wantsShip = payload['shipToAddress'] !== false;
      const activeBuyer = getActiveMockBuyerContact();
      const address = {
        type: 'Address',
        firstName: activeBuyer?.firstName || 'Jordan',
        lastName: activeBuyer?.lastName || 'Hale',
        phoneHome: '555-0100',
        email: activeBuyer?.email || 'jordan.hale@acme.com',
        ...payload,
        // DEMO: Presidio checkout is US-only — keep country fixed even if omitted in the form.
        country: 'United States',
        countryCode: 'US',
        id,
        urn,
        // Apply after payload so billing creates stay invoice-eligible in the Terms picker.
        eligibleInvoiceToAddress: wantsInvoice,
        eligibleShipToAddress: wantsShip,
        invoiceToAddress: wantsInvoice,
        shipToAddress: wantsShip,
        mainDivision: payload['mainDivisionCode'] || payload['mainDivision'] || '',
        street: payload['addressLine1'] || payload['street'] || '',
      };
      const next = [...this.readCreatedAddresses(), address];
      this.writeCreatedAddresses(next);
      return of(
        new HttpResponse({
          status: 201,
          body: {
            type: 'Link',
            uri: `inSPIRED-inTRONICS_Business-Site/-/customers/${customerKey}/addresses/${id}`,
            title: id,
            itemId: id,
          },
        })
      );
    }

    if ((method === 'PUT' || method === 'PATCH') && updateMatch) {
      const customerKey = updateMatch[1] || 'PRESIDIO-MOCK';
      const id = updateMatch[2];
      const payload = this.readJsonBody<Record<string, unknown>>(req.body) || {};
      const created = this.readCreatedAddresses();
      const idx = created.findIndex(a => a.id === id);
      if (idx >= 0) {
        const updated = {
          ...created[idx],
          ...payload,
          id,
          urn: created[idx].urn || `urn_${id.replace(/-/g, '_')}`,
          mainDivision: payload['mainDivisionCode'] || payload['mainDivision'] || created[idx]['mainDivision'],
        };
        created[idx] = updated;
        this.writeCreatedAddresses(created);
        return of(new HttpResponse({ status: 200, body: updated }));
      }
      const staticEntry = MOCK_ADDRESS_BY_ID[id];
      if (staticEntry) {
        const updated = { ...staticEntry.address, ...payload, id, urn: staticEntry.urn };
        // Persist overrides of static addresses as created entries so GETs resolve the edit.
        const without = created.filter(a => a.id !== id);
        this.writeCreatedAddresses([...without, updated]);
        return of(new HttpResponse({ status: 200, body: updated }));
      }
      return of(
        new HttpResponse({
          status: 200,
          body: {
            type: 'Link',
            uri: `inSPIRED-inTRONICS_Business-Site/-/customers/${customerKey}/addresses/${id}`,
            title: id,
            itemId: id,
          },
        })
      );
    }

    return null;
  }

  private readCreatedAddresses(): Record<string, unknown>[] {
    if (typeof sessionStorage === 'undefined') {
      return [];
    }
    try {
      const raw = sessionStorage.getItem(MOCK_CREATED_ADDRESSES_KEY);
      if (!raw) {
        return [];
      }
      const parsed = JSON.parse(raw) as unknown;
      return Array.isArray(parsed) ? (parsed as Record<string, unknown>[]) : [];
    } catch {
      return [];
    }
  }

  private writeCreatedAddresses(addresses: Record<string, unknown>[]): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    sessionStorage.setItem(MOCK_CREATED_ADDRESSES_KEY, JSON.stringify(addresses));
  }

  /**
   * CUSTOMIZATION: After POST …/quotes { quoteRequestID }, mark that QR submitted so the
   * follow-up GET can serve get-submitted.json (static mocks cannot mutate JSON on disk).
   */
  private rememberQuoteSubmit(req: HttpRequest<unknown>, restPath: string): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    if (req.method.toUpperCase() !== 'POST' || !/\/quotes$/.test(restPath) || /\/baskets\//.test(restPath)) {
      return;
    }
    let payload: { quoteRequestID?: string } | null = null;
    if (typeof req.body === 'string') {
      try {
        payload = JSON.parse(req.body) as { quoteRequestID?: string };
      } catch {
        payload = null;
      }
    } else if (req.body && typeof req.body === 'object') {
      payload = req.body as { quoteRequestID?: string };
    }
    const id = payload?.quoteRequestID;
    if (id) {
      sessionStorage.setItem(`${MOCK_QR_SUBMITTED_PREFIX}${id}`, '1');
    }
  }

  private shouldServeSubmittedQuoteRequest(restPath: string, method: string): boolean {
    if (typeof sessionStorage === 'undefined' || method.toUpperCase() !== 'GET') {
      return false;
    }
    const match = /quoterequests\/(MOCK-QR-NEW)$/.exec(restPath);
    if (!match) {
      return false;
    }
    return !!sessionStorage.getItem(`${MOCK_QR_SUBMITTED_PREFIX}${match[1]}`);
  }

  /**
   * CUSTOMIZATION: POST saveforlaterlist → move that one line out of the mock basket
   * and flag SFL list as populated. Do NOT clear the whole cart or force get-empty.json —
   * remaining lines stay via sessionStorage basket state + applyPresidioMockBasketLinesToPayload.
   * POST saveforlaterlist/purchase → re-add the SKU to the mock basket (move back to cart).
   */
  private rememberSaveForLater(req: HttpRequest<unknown>, restPath: string): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    const method = req.method.toUpperCase();
    if (method === 'POST' && restPath === 'saveforlaterlist') {
      sessionStorage.setItem(MOCK_SFL_SAVED_KEY, '1');
      const body = this.readJsonBody<{
        basketLineItemId?: string;
        sku?: string;
        quantity?: number;
      }>(req.body);
      const lineId = body?.basketLineItemId;
      if (lineId) {
        const moved = readPresidioMockBasketLines().find(line => line.id === lineId);
        if (moved) {
          const existing = this.readSaveForLaterLines();
          const withoutSku = existing.filter(line => line.sku !== moved.sku);
          withoutSku.push({
            wishlistLineItemId: `sfl-${moved.id}`,
            sku: moved.sku,
            desiredQuantity: moved.quantity,
            unitPrice: moved.unitPrice,
          });
          sessionStorage.setItem(MOCK_SFL_LINES_KEY, JSON.stringify(withoutSku));
        }
        deletePresidioMockBasketItem(lineId);
      } else if (body?.sku) {
        // Catalog → SFL while quote occupies cart (FR13.7.5) — no basket line to move.
        const existing = this.readSaveForLaterLines();
        const qty = Math.max(1, Math.floor(Number(body.quantity ?? 1)));
        const prior = existing.find(line => line.sku === body.sku);
        const withoutSku = existing.filter(line => line.sku !== body.sku);
        withoutSku.push({
          wishlistLineItemId: prior?.wishlistLineItemId || `sfl-sku-${body.sku}`,
          sku: body.sku,
          desiredQuantity: qty,
          unitPrice: prior?.unitPrice ?? 0,
        });
        sessionStorage.setItem(MOCK_SFL_LINES_KEY, JSON.stringify(withoutSku));
      }
      return;
    }
    if (method === 'DELETE' && restPath === 'saveforlaterlist') {
      sessionStorage.removeItem(MOCK_SFL_SAVED_KEY);
      sessionStorage.removeItem(MOCK_SFL_LINES_KEY);
      return;
    }
    if (method === 'POST' && restPath === 'saveforlaterlist/purchase') {
      const payload = this.readJsonBody<{
        sku?: string;
        quantity?: number;
        product?: string;
        wishlistLineItemId?: string;
      }>(req.body);
      const sku = payload?.sku || payload?.product;
      const stored = this.readSaveForLaterLines();
      const match = sku
        ? stored.find(line => line.sku === sku)
        : stored.find(line => line.wishlistLineItemId === payload?.wishlistLineItemId);
      const qty = Math.max(1, Math.floor(Number(payload?.quantity ?? match?.desiredQuantity ?? 1)));
      const productSku = sku || match?.sku;
      if (productSku) {
        addPresidioMockBasketItems({
          product: productSku,
          quantity: { value: qty, unit: 'pcs' },
        });
        const remaining = stored.filter(line => line.sku !== productSku);
        if (remaining.length) {
          sessionStorage.setItem(MOCK_SFL_LINES_KEY, JSON.stringify(remaining));
          sessionStorage.setItem(MOCK_SFL_SAVED_KEY, '1');
        } else {
          sessionStorage.removeItem(MOCK_SFL_LINES_KEY);
          sessionStorage.removeItem(MOCK_SFL_SAVED_KEY);
        }
      } else {
        sessionStorage.removeItem(MOCK_SFL_SAVED_KEY);
        sessionStorage.removeItem(MOCK_SFL_LINES_KEY);
      }
    }
  }

  private readSaveForLaterLines(): Array<{
    wishlistLineItemId: string;
    sku: string;
    desiredQuantity: number;
    unitPrice: number;
  }> {
    if (typeof sessionStorage === 'undefined') {
      return [];
    }
    try {
      const raw = sessionStorage.getItem(MOCK_SFL_LINES_KEY);
      if (!raw) {
        return [];
      }
      const parsed = JSON.parse(raw) as Array<{
        wishlistLineItemId: string;
        sku: string;
        desiredQuantity: number;
        unitPrice: number;
      }>;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private shouldServeSavedSaveForLater(restPath: string, method: string): boolean {
    return (
      typeof sessionStorage !== 'undefined' &&
      method.toUpperCase() === 'GET' &&
      restPath === 'saveforlaterlist' &&
      !!sessionStorage.getItem(MOCK_SFL_SAVED_KEY)
    );
  }

  /** CUSTOMIZATION: rewrite get-saved SFL payload from session snapshot (qty / multi-line). */
  private applySaveForLaterSessionOverrides(restPath: string, method: string, body: unknown): unknown {
    if (
      method.toUpperCase() !== 'GET' ||
      restPath !== 'saveforlaterlist' ||
      !sessionStorage.getItem(MOCK_SFL_SAVED_KEY)
    ) {
      return body;
    }
    const lines = this.readSaveForLaterLines();
    if (!lines.length) {
      return body;
    }
    return {
      wishlistId: 'sfl-mock-list-1',
      lineItems: lines.map(line => ({
        wishlistLineItemId: line.wishlistLineItemId,
        desiredQuantity: line.desiredQuantity,
        sku: line.sku,
        price: {
          type: 'Money',
          value: line.unitPrice,
          currency: 'USD',
        },
        totalPrice: {
          type: 'Money',
          value: line.unitPrice * line.desiredQuantity,
          currency: 'USD',
        },
      })),
    };
  }

  /**
   * CUSTOMIZATION: PATCH baskets/{id} with invoiceToAddress / commonShipToAddress persists
   * the chosen address ids so subsequent get/patch mocks keep the selection.
   * Accepts address id or urn (eligible-address payloads vary).
   */
  private rememberBasketAddressAssign(req: HttpRequest<unknown>, restPath: string): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    if (req.method.toUpperCase() !== 'PATCH') {
      return;
    }
    if (restPath !== 'baskets/current' && restPath !== 'baskets/MOCK-BASKET-001') {
      return;
    }
    const payload = this.readJsonBody<{ invoiceToAddress?: string; commonShipToAddress?: string }>(req.body);
    if (!payload) {
      return;
    }
    const invoiceKey = payload.invoiceToAddress ? this.resolveMockAddressId(payload.invoiceToAddress) : null;
    const shippingKey = payload.commonShipToAddress
      ? this.resolveMockAddressId(payload.commonShipToAddress)
      : null;
    if (invoiceKey) {
      sessionStorage.setItem(MOCK_BASKET_INVOICE_ADDR_KEY, invoiceKey);
    }
    if (shippingKey) {
      sessionStorage.setItem(MOCK_BASKET_SHIPPING_ADDR_KEY, shippingKey);
      // Do not auto-select a shipping method — shopper chooses among eligible methods.
    }
  }

  /** Resolve mock address catalog id from an id or urn reference. */
  private resolveMockAddressId(ref: string): string | null {
    if (!ref) {
      return null;
    }
    if (MOCK_ADDRESS_BY_ID[ref]) {
      return ref;
    }
    const byUrn = Object.entries(MOCK_ADDRESS_BY_ID).find(([, entry]) => entry.urn === ref);
    if (byUrn) {
      return byUrn[0];
    }
    const created = this.readCreatedAddresses().find(
      a => String(a.id) === ref || String(a.urn || '') === ref
    );
    return created ? String(created.id) : null;
  }

  /**
   * CUSTOMIZATION: rebuild facets from the products fixture (searchTerm + catalog), then
   * mark selected filterEntries and rewrite link URIs so apply/deselect works in demo.
   * Reads both the request URL query string and HttpParams (ApiService often uses params).
   */
  private applyProductFilterSelection(req: HttpRequest<unknown>, restPath: string, body: unknown): unknown {
    // restPath is already matrix-stripped via getRestPath; keep a local alias for clarity.
    const path = restPath || '';
    if (
      req.method.toUpperCase() !== 'GET' ||
      !body ||
      typeof body !== 'object' ||
      (!path.endsWith('/productfilters') && path !== 'productfilters')
    ) {
      return body;
    }

    const selected = this.parseFilterQueryParams(req);
    const searchTerm = (
      this.readRequestParam(req, 'searchTerm') ||
      this.readRequestParam(req, 'SearchTerm') ||
      ''
    ).trim();

    // Category PLPs (including nested leaves like HW/PORT) — rebuild facets from that
    // category's products fixture so Manufacturer/Family counts match the latest catalog.
    const categoryMatch = /^categories\/(.+)\/productfilters$/.exec(path);
    if (categoryMatch) {
      const categoryPath = categoryMatch[1];
      const categoryProducts = getCategoryProductsFixture(categoryPath);
      if (categoryProducts) {
        return buildProductFiltersFromProductStubs(categoryProducts, {
          searchTerm,
          selected,
          filterBasePath: `categories/${categoryPath}/productfilters`,
          // Category product lists are already scoped; WH is held-stock only (MVP 2.6.3)
          skipCatalogFilter: true,
        });
      }
      // Unknown category path: keep static fixture selection marking
      return this.applySelectionToStaticProductFilters(body, selected, path);
    }

    // Search / master productfilters — rebuild from catalog product stubs
    return buildProductFiltersFromProductStubs(
      productsListFixture as {
        elements?: Array<{
          title?: string;
          description?: string;
          attributes?: Array<{ name?: string; value?: unknown }>;
        }>;
      },
      { searchTerm, selected }
    );
  }

  /**
   * DEMO: mark selected entries on a category's static productfilters fixture
   * without replacing facet values with the global catalog.
   */
  private applySelectionToStaticProductFilters(
    body: unknown,
    selected: Record<string, string[]>,
    filterBasePath: string
  ): unknown {
    if (!body || typeof body !== 'object') {
      return body;
    }
    const clone = JSON.parse(JSON.stringify(body)) as {
      elements?: Array<{
        id?: string;
        filterEntries?: Array<{
          name?: string;
          selected?: boolean;
          link?: { uri?: string; type?: string; title?: string };
        }>;
      }>;
    };
    if (!Array.isArray(clone.elements)) {
      return body;
    }
    for (const filter of clone.elements) {
      const key = filter.id || '';
      const selectedValues = selected[key] || selected[key.toLowerCase()] || [];
      for (const entry of filter.filterEntries || []) {
        const name = entry.name || '';
        const isSelected = selectedValues.some(
          v => v === name || decodeURIComponent(v) === name || v.toLowerCase() === name.toLowerCase()
        );
        entry.selected = isSelected;
        const params = new URLSearchParams();
        const nextSelected = { ...selected };
        if (isSelected) {
          nextSelected[key] = (nextSelected[key] || []).filter(
            v => v !== name && decodeURIComponent(v) !== name
          );
          if (!nextSelected[key]?.length) {
            delete nextSelected[key];
          }
        } else if (key) {
          nextSelected[key] = [...(nextSelected[key] || []).filter(v => v !== name), name];
        }
        Object.entries(nextSelected).forEach(([paramKey, values]) => {
          if (values?.length) {
            params.set(paramKey, values.join(','));
          }
        });
        const query = params.toString();
        entry.link = {
          type: 'Link',
          uri: `inSPIRED-inTRONICS_Business-Site/-/${filterBasePath}${query ? `?${query}` : ''}`,
          title: name,
        };
      }
    }
    return clone;
  }

  private applyCustomSuggestFilter(
    req: HttpRequest<unknown>,
    restPath: string,
    body: unknown,
    responseUrl = ''
  ): unknown {
    if (
      req.method.toUpperCase() !== 'GET' ||
      !restPath.startsWith('customsuggest') ||
      !body ||
      typeof body !== 'object'
    ) {
      return body;
    }

    const needle = (
      req.params.get('SearchTerm') ||
      req.params.get('searchTerm') ||
      this.readQueryParam(req.urlWithParams || req.url, 'SearchTerm') ||
      this.readQueryParam(req.urlWithParams || req.url, 'searchTerm') ||
      this.readQueryParam(responseUrl, 'SearchTerm') ||
      this.readQueryParam(responseUrl, 'searchTerm') ||
      ''
    )
      .trim()
      .toLowerCase();

    // Keep full fixture when term is missing (avoid wiping static mock on mis-parse)
    if (!needle) {
      return body;
    }

    const clone = JSON.parse(JSON.stringify(body)) as {
      elements?: Array<{ term?: string; id?: string; detail?: string; type?: string }>;
    };
    if (!clone.elements?.length) {
      return body;
    }

    // Match product name, manufacturer SKU (catalog id === manufacturerSKU; no Presidio SKU),
    // brand in detail, type, and UNSPSC via sku→UNSPSC map. Multi-word queries require every token.
    // Exact manufacturer SKU hits rank first (MVP 2.1.3 part # / manufacturer SKU search).
    // Product rows are catalog-scoped like the product list so typeahead counts match Enter.
    const tokens = needle.split(/\s+/).filter(Boolean);
    const resolvedId = (resolvePresidioProductSku(needle) || '').trim().toLowerCase();
    const scored = clone.elements
      .map(el => {
        const type = (el.type || '').toLowerCase();
        const id = (el.id || '').toLowerCase();
        if (type === 'product' && el.id && !isSkuAllowedForActiveCustomer(el.id)) {
          return null;
        }
        const unspsc = lookupPresidioUnspsc(el.id);
        // Keep UNSPSC out of the free-text haystack so short tokens cannot substring-match codes.
        const haystack = `${el.term || ''} ${el.id || ''} ${el.detail || ''} ${el.type || ''}`.toLowerCase();
        const matches = tokens.every(token => {
          if (haystack.includes(token)) {
            return true;
          }
          return unspscMatchesSearchToken(unspsc, token);
        });
        if (!matches) {
          return null;
        }
        const exactId = !!id && (id === needle || (!!resolvedId && id === resolvedId));
        return { el, exactId };
      })
      .filter((row): row is { el: (typeof clone.elements)[number]; exactId: boolean } => !!row)
      .sort((a, b) => Number(b.exactId) - Number(a.exactId));

    clone.elements = scored.map(row => row.el).slice(0, 8);

    return clone;
  }

  private parseFilterQueryParams(req: HttpRequest<unknown>): Record<string, string[]> {
    const selected: Record<string, string[]> = {};
    const skip = new Set([
      'searchTerm',
      'MasterSKU',
      'spgid',
      'loc',
      'cur',
      'amount',
      'offset',
      'attrs',
      'attributeGroup',
      'returnSortKeys',
      'sortKey',
      'pgid',
    ]);

    const addParam = (key: string, value: string) => {
      if (!key || skip.has(key) || !value) {
        return;
      }
      selected[key] = selected[key] || [];
      value.split(',').forEach(part => {
        const trimmed = part.trim();
        if (trimmed && !selected[key].includes(trimmed)) {
          selected[key].push(trimmed);
        }
      });
    };

    const queryIndex = req.url.indexOf('?');
    if (queryIndex >= 0) {
      new URLSearchParams(req.url.substring(queryIndex + 1)).forEach((value, key) => addParam(key, value));
    }

    req.params?.keys()?.forEach(key => {
      req.params.getAll(key)?.forEach(value => addParam(key, value));
    });

    return selected;
  }

  /**
   * ICM product list URLs often look like `products;spgid=…` or
   * `categories;spgid=…/HW/products` (matrix params). getRestPath strips them;
   * keep a defensive strip here for any caller that still passes a raw path.
   */
  private isProductsListRestPath(restPath: string): boolean {
    const path = (restPath || '').replace(/;[^/]*/g, '');
    return path === 'products' || path.endsWith('/products');
  }

  /**
   * CUSTOMIZATION: optionally narrow static products mocks by searchTerm / Manufacturer /
   * ProductFamily / MasterSKU / InMyWarehouse. Search matches title, sku, mfr part #,
   * manufacturer, ProductFamily (aligned with typeahead — not free-form long description).
   */
  private applyProductListFilter(req: HttpRequest<unknown>, restPath: string, body: unknown): unknown {
    if (req.method.toUpperCase() !== 'GET' || !body || typeof body !== 'object' || !this.isProductsListRestPath(restPath)) {
      return body;
    }

    const selected = this.parseFilterQueryParams(req);
    const manufacturers = selected.Manufacturer || selected.manufacturer || [];
    const families = selected.ProductFamily || selected.productFamily || [];
    const inMyWarehouse =
      isPresidioMockWarehouseLogin() && isInMyWarehouseFilterSelected(selected);
    const masterSku = this.readRequestParam(req, 'MasterSKU');
    const searchTerm = (
      this.readRequestParam(req, 'searchTerm') ||
      this.readRequestParam(req, 'SearchTerm') ||
      ''
    )
      .trim()
      .toLowerCase();

    if (!manufacturers.length && !families.length && !masterSku && !searchTerm && !inMyWarehouse) {
      return body;
    }

    const clone = JSON.parse(JSON.stringify(body)) as {
      elements?: Array<{
        title?: string;
        description?: string;
        attributes?: Array<{ name?: string; value?: unknown }>;
      }>;
      total?: number;
    };
    if (!clone.elements?.length) {
      return body;
    }

    let elements = clone.elements;

    if (searchTerm) {
      // Manufacturer SKU + UNSPSC + title/brand/family (shared matcher; no separate Presidio SKU).
      elements = elements.filter(el => productStubMatchesSearchTerm(el, searchTerm));
    }

    if (masterSku) {
      elements = elements.filter(el => {
        const attrs = el.attributes || [];
        const sku = attrs.find(a => a.name === 'sku')?.value;
        const productMasterSKU = attrs.find(a => a.name === 'productMasterSKU')?.value;
        const productMaster = attrs.find(a => a.name === 'productMaster')?.value;
        // Variation children of this master, or the master itself
        return productMasterSKU === masterSku || (productMaster === true && sku === masterSku);
      });
    }

    if (manufacturers.length) {
      const wanted = manufacturers.map(m => m.toLowerCase());
      elements = elements.filter(el => {
        const manufacturer = el.attributes?.find(a => a.name?.toLowerCase() === 'manufacturer')?.value;
        // When a Manufacturer facet is selected, omit stubs that lack a brand match.
        if (typeof manufacturer !== 'string') {
          return false;
        }
        const value = manufacturer.toLowerCase();
        return wanted.some(w => value === w);
      });
    }

    if (families.length) {
      const wanted = families.map(f => f.toLowerCase());
      elements = elements.filter(el => {
        const family = el.attributes?.find(a => a.name?.toLowerCase() === 'productfamily')?.value;
        if (typeof family !== 'string') {
          return false;
        }
        const value = family.toLowerCase();
        return wanted.some(w => value === w);
      });
    }

    // MVP 2.6.3 / FR5.3.6 — warehouse accounts: In My Warehouse → held SKUs only
    if (inMyWarehouse) {
      elements = filterProductListElementsByInMyWarehouse(elements);
    }

    clone.elements = elements;
    clone.total = elements.length;
    return clone;
  }

  /**
   * DEMO-ONLY: slice static product list fixtures by ICM amount/offset so endless scrolling
   * (DIAS default `mode="endless-scrolling"`) loads pages incrementally instead of all at once.
   */
  private applyProductListPagination(req: HttpRequest<unknown>, restPath: string, body: unknown): unknown {
    if (req.method.toUpperCase() !== 'GET' || !body || typeof body !== 'object' || !this.isProductsListRestPath(restPath)) {
      return body;
    }

    const amountRaw = this.readRequestParam(req, 'amount');
    if (!amountRaw) {
      return body;
    }

    const amount = Number.parseInt(amountRaw, 10);
    if (!Number.isFinite(amount) || amount <= 0) {
      return body;
    }

    const offsetRaw = this.readRequestParam(req, 'offset');
    const offset = Number.parseInt(offsetRaw || '0', 10);
    const start = Number.isFinite(offset) && offset > 0 ? offset : 0;

    const clone = JSON.parse(JSON.stringify(body)) as {
      elements?: unknown[];
      total?: number;
      amount?: number;
    };
    if (!Array.isArray(clone.elements)) {
      return body;
    }

    const total = typeof clone.total === 'number' ? clone.total : clone.elements.length;
    clone.elements = clone.elements.slice(start, start + amount);
    clone.total = total;
    clone.amount = clone.elements.length;
    return clone;
  }

  /** Read a single query/HttpParams value (MasterSKU, etc.). */
  private readRequestParam(req: HttpRequest<unknown>, key: string): string | undefined {
    const fromParams = req.params?.get(key);
    if (fromParams) {
      return fromParams;
    }
    const queryIndex = req.url.indexOf('?');
    if (queryIndex < 0) {
      return undefined;
    }
    return new URLSearchParams(req.url.substring(queryIndex + 1)).get(key) || undefined;
  }

  private rememberBasketShippingMethodAssign(req: HttpRequest<unknown>, restPath: string): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    if (req.method.toUpperCase() !== 'PATCH') {
      return;
    }
    if (restPath !== 'baskets/current' && restPath !== 'baskets/MOCK-BASKET-001') {
      return;
    }
    const payload = this.readJsonBody<{ commonShippingMethod?: string }>(req.body);
    if (payload?.commonShippingMethod) {
      sessionStorage.setItem(MOCK_BASKET_SHIPPING_METHOD_KEY, payload.commonShippingMethod);
    }
  }

  /**
   * CUSTOMIZATION: PATCH baskets/{id} with recurrence persists schedule so frequency
   * form stays open after selecting Recurring order (static patch.json has no recurrence).
   * Explicit null clears it (One-time purchase).
   */
  private rememberBasketRecurrenceAssign(req: HttpRequest<unknown>, restPath: string): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    if (req.method.toUpperCase() !== 'PATCH') {
      return;
    }
    if (restPath !== 'baskets/current' && restPath !== 'baskets/MOCK-BASKET-001') {
      return;
    }
    const payload = this.readJsonBody<{ recurrence?: Record<string, unknown> | null }>(req.body);
    if (!payload || !('recurrence' in payload)) {
      return;
    }
    if (payload.recurrence == null) {
      sessionStorage.removeItem(MOCK_BASKET_RECURRENCE_KEY);
      return;
    }
    sessionStorage.setItem(MOCK_BASKET_RECURRENCE_KEY, JSON.stringify(payload.recurrence));
  }

  private rememberBasketPaymentAssign(req: HttpRequest<unknown>, restPath: string): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    if (!/baskets\/(?:current|MOCK-BASKET-001)\/payments\/open-tender$/.test(restPath)) {
      return;
    }
    const method = req.method.toUpperCase();
    if (method === 'PUT' || method === 'PATCH') {
      const payload = this.readJsonBody<{ paymentInstrument?: string }>(req.body);
      sessionStorage.setItem(MOCK_BASKET_PAYMENT_KEY, payload?.paymentInstrument || 'ISH_INVOICE');
      return;
    }
    if (method === 'DELETE') {
      sessionStorage.setItem(MOCK_BASKET_PAYMENT_KEY, '');
    }
  }

  /**
   * CUSTOMIZATION: POST accessrequests/verify — confirm ERP before remaining fields.
   * DEMO: treat INVALID / NOTFOUND / UNKNOWN as missing; all other non-empty ERPs succeed.
   */
  private handleAccessRequestVerify(req: HttpRequest<unknown>, restPath: string): Observable<HttpEvent<unknown>> | null {
    if (req.method.toUpperCase() !== 'POST' || restPath !== 'accessrequests/verify') {
      return null;
    }
    const body = this.readJsonBody<{ erpCustomerNo?: string }>(req.body) || {};
    const erp = String(body.erpCustomerNo || '')
      .trim()
      .toUpperCase();
    if (!erp) {
      return throwError(
        () =>
          new HttpErrorResponse({
            status: 400,
            error: { error: 'ERP customer number is required' },
            url: req.url,
          })
      );
    }

    const classified = classifyPresidioAccessErp(erp);
    if (!classified.found) {
      return throwError(
        () =>
          new HttpErrorResponse({
            status: 404,
            error: {
              error: 'ERP customer number not found',
              messageKey: 'account.request_access.erp.error.contact_sales',
            },
            url: req.url,
          })
      );
    }

    return of(
      new HttpResponse({
        status: 200,
        body: {
          ok: !classified.contactSalesRep,
          erpCustomerNo: erp,
          companyName: classified.companyName,
          hasAdministrators: classified.hasAdministrators,
          hasAssignedCsr: classified.hasAssignedCsr,
          contactSalesRep: classified.contactSalesRep,
        },
        url: req.url,
      })
    );
  }

  /**
   * CUSTOMIZATION: POST accessrequests — demo success for Request access (no ICM yet).
   */
  /**
   * CUSTOMIZATION: MVP 2.2.1 — cart pricing requests to sales (PROVISIONAL `pricingrequests` API).
   */
  private handlePricingRequest(req: HttpRequest<unknown>, restPath: string): Observable<HttpEvent<unknown>> | null {
    const detailMatch = /^pricingrequests\/([^/]+)$/.exec(restPath);
    if (detailMatch) {
      if (req.method.toUpperCase() !== 'GET') {
        return null;
      }
      const found = findPresidioMockPricingRequest(decodeURIComponent(detailMatch[1]));
      if (!found) {
        return throwError(
          () =>
            new HttpErrorResponse({
              status: 404,
              error: { error: 'Pricing request not found' },
              url: req.url,
            })
        );
      }
      return of(new HttpResponse({ status: 200, body: found, url: req.url }));
    }

    if (restPath !== 'pricingrequests') {
      return null;
    }
    const method = req.method.toUpperCase();
    if (method === 'GET') {
      return of(
        new HttpResponse({
          status: 200,
          body: { elements: readPresidioMockPricingRequests() },
          url: req.url,
        })
      );
    }
    if (method !== 'POST') {
      return null;
    }

    const body =
      this.readJsonBody<{
        title?: string;
        comment?: string;
        attachmentName?: string;
        fromBasket?: boolean;
        basketId?: string;
        affiliatedCompany?: string;
        items?: { sku: string; name?: string; quantity: number; unitPrice?: number }[];
      }>(req.body) || {};

    if (body.comment?.trim().toUpperCase() === 'FAIL-DEMO') {
      return throwError(
        () =>
          new HttpErrorResponse({
            status: 500,
            error: { error: 'Pricing request could not be submitted' },
            url: req.url,
          })
      );
    }

    const basketItems =
      body.fromBasket && hasPresidioMockBasketLines()
        ? readPresidioMockBasketLines().map(line => ({
            sku: line.sku,
            quantity: line.quantity,
            unitPrice: line.unitPrice,
          }))
        : undefined;

    const existing = readPresidioMockPricingRequests();
    const pr = {
      id: `PR-${Date.now()}`,
      number: `PR-${2200 + existing.length}`,
      title: body.title || 'Pricing request',
      requestor: 'Jordan Hale',
      status: 'Open' as const,
      creationDate: new Date().toISOString(),
      comment: body.comment,
      attachmentName: body.attachmentName,
      basketId: body.basketId,
      fromBasket: body.fromBasket,
      affiliatedCompany: body.affiliatedCompany?.trim() || getPresidioMockActiveCompanyName(),
      items: body.items?.length ? body.items : basketItems,
      total: body.fromBasket && hasPresidioMockBasketLines() ? this.estimateMockBasketGrandTotal() : undefined,
    };
    appendPresidioMockPricingRequest(pr);

    return of(new HttpResponse({ status: 200, body: pr, url: req.url }));
  }

  /** DEMO: rough cart total for pricing-request confirmation context. */
  private estimateMockBasketGrandTotal(): number | undefined {
    const lines = readPresidioMockBasketLines();
    if (!lines.length) {
      return undefined;
    }
    return lines.reduce((sum, line) => sum + line.unitPrice * line.quantity, 0);
  }

  /**
   * CUSTOMIZATION: POST accessrequests/check-email — early domain allowlist check (FR1.3.8).
   * Returns `{ ok }` only; never lists approved domains.
   */
  private handleAccessRequestCheckEmail(
    req: HttpRequest<unknown>,
    restPath: string
  ): Observable<HttpEvent<unknown>> | null {
    if (req.method.toUpperCase() !== 'POST' || restPath !== 'accessrequests/check-email') {
      return null;
    }
    const body = this.readJsonBody<{ erpCustomerNo?: string; email?: string }>(req.body) || {};
    const email = String(body.email || '')
      .trim()
      .toLowerCase();
    const erp = String(body.erpCustomerNo || '').trim();
    if (!email || !erp) {
      return of(new HttpResponse({ status: 200, body: { ok: false }, url: req.url }));
    }

    const classified = classifyPresidioAccessErp(erp);
    if (!classified.found || classified.contactSalesRep) {
      return of(new HttpResponse({ status: 200, body: { ok: false }, url: req.url }));
    }

    const domainCustomerNo = classified.domainCustomerNo;
    if (domainCustomerNo) {
      const approved = getApprovedEmailDomainsByCustomer()[domainCustomerNo];
      if (!isEmailAllowedForApprovedDomains(email, approved)) {
        return of(new HttpResponse({ status: 200, body: { ok: false }, url: req.url }));
      }
    }

    return of(new HttpResponse({ status: 200, body: { ok: true }, url: req.url }));
  }

  private handleAccessRequest(req: HttpRequest<unknown>, restPath: string): Observable<HttpEvent<unknown>> | null {
    if (req.method.toUpperCase() !== 'POST' || restPath !== 'accessrequests') {
      return null;
    }
    const body =
      this.readJsonBody<{
        erpCustomerNo?: string;
        firstName?: string;
        lastName?: string;
        email?: string;
        password?: string;
        department?: string;
        title?: string;
        phone?: string;
      }>(req.body) || {};
    if (!body.erpCustomerNo || !body.firstName || !body.lastName || !body.email || !body.password) {
      return throwError(
        () =>
          new HttpErrorResponse({
            status: 400,
            error: { error: 'Missing required access request fields' },
            url: req.url,
          })
      );
    }

    const email = String(body.email).trim().toLowerCase();
    if (isPresidioMockEmailAlreadyRegistered(email)) {
      return throwError(
        () =>
          new HttpErrorResponse({
            status: 409,
            error: {
              // `code` survives ICMErrorMapperInterceptor; used as i18n key in the form.
              code: 'account.request_access.error.email_exists',
              message: 'Email already registered',
              messageKey: 'account.request_access.error.email_exists',
            },
            url: req.url,
          })
      );
    }

    const classified = classifyPresidioAccessErp(String(body.erpCustomerNo));
    if (!classified.found || classified.contactSalesRep) {
      return throwError(
        () =>
          new HttpErrorResponse({
            status: 422,
            error: {
              error: 'Contact your Presidio sales representative',
              messageKey: 'account.request_access.erp.error.contact_sales',
            },
            url: req.url,
          })
      );
    }

    // MVP 1.2.1 — when the account has approved email domains, reject other domains
    // without revealing the allow-list (requestor should contact their administrator).
    const domainCustomerNo = classified.domainCustomerNo;
    if (domainCustomerNo) {
      const approved = getApprovedEmailDomainsByCustomer()[domainCustomerNo];
      if (!isEmailAllowedForApprovedDomains(email, approved)) {
        return throwError(
          () =>
            new HttpErrorResponse({
              status: 422,
              error: {
                code: 'account.request_access.error.email_domain',
                message: 'Email domain not allowed for this account',
                messageKey: 'account.request_access.error.email_domain',
              },
              url: req.url,
            })
        );
      }
    }

    const requestedRole =
      String((body as { requestedRole?: string }).requestedRole || '').toLowerCase() === 'administrator'
        ? 'administrator'
        : 'standard';
    const { route, reviewerLabel } = recordDiscoveryAccessRequest({
      email: body.email,
      firstName: body.firstName,
      lastName: body.lastName,
      companyName: classified.companyName || body.erpCustomerNo,
      requestedRole,
      hasAdministrators: classified.hasAdministrators,
      hasAssignedCsr: classified.hasAssignedCsr,
      salesRepName: classified.csrName,
      department: body.department,
      title: body.title,
      phone: body.phone,
    });

    return of(
      new HttpResponse({
        status: 200,
        body: {
          ok: true,
          route,
          pendingApproval: true,
          reviewerLabel,
          messageKey:
            route === 'admin'
              ? 'account.request_access.success.admin'
              : 'account.request_access.success.sales',
          detailKey:
            route === 'admin'
              ? 'account.request_access.success.admin.pending'
              : 'account.request_access.success.sales.pending',
        },
        url: req.url,
      })
    );
  }

  /**
   * CUSTOMIZATION: FR 1.4.7 — password login always requires email OTP unless device is trusted (90 days).
   * Demo OTP: 123456. Code validity messaging is 15 minutes. Anonymous / refresh grants unchanged.
   */
  private handleTokenMfa(req: HttpRequest<unknown>, restPath: string): Observable<HttpEvent<unknown>> | null {
    if (req.method.toUpperCase() !== 'POST' || restPath !== 'token') {
      return null;
    }
    if (!this.isPasswordGrant(req)) {
      return null;
    }

    const username = this.readPasswordGrantUsername(req);
    const loginBlock = getDiscoveryLoginBlockReason(username);
    if (loginBlock === 'pending') {
      return throwError(
        () =>
          new HttpErrorResponse({
            status: 403,
            error: {
              type: 'LoginErrorRO',
              error: 'USER_PENDING_APPROVAL',
              messageKey: 'account.login.pending_approval.error',
            },
            url: req.url,
          })
      );
    }
    if (loginBlock === 'disabled') {
      return throwError(
        () =>
          new HttpErrorResponse({
            status: 403,
            error: {
              type: 'LoginErrorRO',
              error: 'USER_DISABLED',
              messageKey: 'account.login.disabled_user.error',
            },
            url: req.url,
          })
      );
    }

    if (username && this.isMfaDeviceTrusted(username)) {
      setPresidioMockActiveLogin(username);
      return of(
        new HttpResponse({
          status: 200,
          body: {
            access_token: 'presidio-mock-access-token',
            token_type: 'Bearer',
            expires_in: 3600,
            refresh_token: 'presidio-mock-refresh-token',
            scope: 'openid',
          },
          url: req.url,
        })
      );
    }

    const mfaCode = req.headers.get('X-MFA-Code');
    const challengeId = req.headers.get('X-MFA-Challenge-Id');

    if (mfaCode && challengeId) {
      // DEMO codes (not business requirements): 123456 valid; 000000 expired; other invalid.
      if (mfaCode === '123456') {
        setPresidioMockActiveLogin(username);
        return of(
          new HttpResponse({
            status: 200,
            body: {
              access_token: 'presidio-mock-access-token',
              token_type: 'Bearer',
              expires_in: 3600,
              refresh_token: 'presidio-mock-refresh-token',
              scope: 'openid',
            },
            url: req.url,
          })
        );
      }
      if (mfaCode === '000000') {
        return throwError(
          () =>
            new HttpErrorResponse({
              status: 401,
              error: {
                type: 'MfaErrorRO',
                error: 'EXPIRED_MFA_CODE',
                messageKey: 'account.login.otp.error.expired',
              },
              url: req.url,
            })
        );
      }
      return throwError(
        () =>
          new HttpErrorResponse({
            status: 401,
            error: {
              type: 'MfaErrorRO',
              error: 'INVALID_MFA_CODE',
              messageKey: 'account.login.otp.error.invalid',
            },
            url: req.url,
          })
      );
    }

    return throwError(
      () =>
        new HttpErrorResponse({
          status: 401,
          error: {
            type: 'MfaChallengeRO',
            error: 'MFA_REQUIRED',
            challengeId: 'presidio-demo-challenge',
            expiresAt: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
            otpLength: 6,
          },
          url: req.url,
        })
    );
  }

  /**
   * OAuth password grants post `HttpParams` (angular-oauth2-oidc), not a plain object/string.
   */
  private readTokenFormParam(req: HttpRequest<unknown>, key: string): string {
    const body = req.body;
    if (body == null) {
      return '';
    }
    if (typeof body === 'string') {
      try {
        return (new URLSearchParams(body).get(key) || '').trim();
      } catch {
        return '';
      }
    }
    if (typeof (body as { get?: (k: string) => string | null }).get === 'function') {
      return String((body as { get: (k: string) => string | null }).get(key) || '').trim();
    }
    if (typeof (body as { toString?: () => string }).toString === 'function') {
      const asString = (body as { toString: () => string }).toString();
      if (asString && asString !== '[object Object]') {
        try {
          return (new URLSearchParams(asString).get(key) || '').trim();
        } catch {
          /* fall through */
        }
      }
    }
    if (typeof body === 'object' && key in (body as object)) {
      return String((body as Record<string, unknown>)[key] ?? '').trim();
    }
    return '';
  }

  private isPasswordGrant(req: HttpRequest<unknown>): boolean {
    return this.readTokenFormParam(req, 'grant_type') === 'password';
  }

  private readPasswordGrantUsername(req: HttpRequest<unknown>): string {
    return this.readTokenFormParam(req, 'username');
  }

  private isMfaDeviceTrusted(username: string): boolean {
    if (typeof localStorage === 'undefined') {
      return false;
    }
    try {
      const until = localStorage.getItem(MfaDeviceTrustService.storageKeyFor(username));
      return !!until && Date.parse(until) > Date.now();
    } catch {
      return false;
    }
  }

  /**
   * CUSTOMIZATION: FR 1.4.7 — preference always enabled for password login (no end-user toggle in MVP UX).
   */
  private handleTwoStepVerification(req: HttpRequest<unknown>, restPath: string): Observable<HttpEvent<unknown>> | null {
    if (restPath !== 'customers/-/users/-/twostepverification') {
      return null;
    }
    const method = req.method.toUpperCase();
    if (method === 'GET' || method === 'PUT' || method === 'PATCH') {
      return of(new HttpResponse({ status: 200, body: { enabled: true }, url: req.url }));
    }
    return null;
  }

  /** CUSTOMIZATION: FR 1.4.7 — demo mfa-resend always succeeds. */
  private handleMfaResend(req: HttpRequest<unknown>, restPath: string): Observable<HttpEvent<unknown>> | null {
    if (req.method.toUpperCase() !== 'POST' || restPath !== 'mfa-resend') {
      return null;
    }
    return of(new HttpResponse({ status: 200, body: {}, url: req.url }));
  }

  /**
   * CUSTOMIZATION: PUT token/logout — demo revoke so logout clears session.
   */
  private handleTokenLogout(req: HttpRequest<unknown>, restPath: string): Observable<HttpEvent<unknown>> | null {
    if (req.method.toUpperCase() !== 'PUT' || restPath !== 'token/logout') {
      return null;
    }
    clearPresidioMockActiveLogin();
    return of(new HttpResponse({ status: 200, body: {}, url: req.url }));
  }

  /**
   * DEMO MVP 8.2.1 / 8.2.2 HTTP handlers live in `presidio-mock-ops-handlers.ts`
   * (affiliate switch, CSR impersonate start/end, checkout block while co-browsing).
   */

  /**
   * CUSTOMIZATION: GET baskets/.../buckets/{id}/eligible-shipping-methods
   * Multi-ship loadBasketSuccess fans out per bucket; without a fixture the request 404s
   * and can leave checkout on the full-page spinner. Reuse basket-level methods payload.
   */
  private handleBucketEligibleShippingMethods(
    req: HttpRequest<unknown>,
    restPath: string
  ): Observable<HttpEvent<unknown>> | null {
    if (req.method.toUpperCase() !== 'GET') {
      return null;
    }
    const match = /^baskets\/(?:current|MOCK-BASKET-001)\/buckets\/[^/]+\/eligible-shipping-methods$/.exec(restPath);
    if (!match) {
      return null;
    }
    return of(
      new HttpResponse({
        status: 200,
        url: req.url,
        body: {
          data: [
            {
              id: 'STD_GROUND',
              name: 'Standard Ground',
              description: '5-7 business days',
              deliveryTimeMin: 'P5D',
              deliveryTimeMax: 'P7D',
              shippingCosts: {
                gross: { value: 249, currency: 'USD' },
                net: { value: 249, currency: 'USD' },
              },
            },
            {
              id: 'EXP_2DAY',
              name: 'Express 2-Day',
              description: '2 business days',
              deliveryTimeMin: 'P2D',
              deliveryTimeMax: 'P2D',
              shippingCosts: {
                gross: { value: 499, currency: 'USD' },
                net: { value: 499, currency: 'USD' },
              },
            },
          ],
        },
      })
    );
  }

  /**
   * CUSTOMIZATION: mutable basket items — POST add, PATCH qty, DELETE remove.
   * Static fixtures always returned the same seeded line; this makes ATC + mini-cart qty work.
   */
  private handleBasketItemMutation(
    req: HttpRequest<unknown>,
    restPath: string
  ): Observable<HttpEvent<unknown>> | null {
    const collection = /^baskets\/(?:current|MOCK-BASKET-001)\/items$/.exec(restPath);
    const single = /^baskets\/(?:current|MOCK-BASKET-001)\/items\/([^/]+)$/.exec(restPath);
    const separate = /^baskets\/(?:current|MOCK-BASKET-001)\/items\/([^/]+)\/separate-lineitem$/.exec(restPath);
    const method = req.method.toUpperCase();

    if (collection && method === 'POST') {
      const payload = this.readJsonBody<{ elements?: { product?: string }[]; product?: string }>(req.body);
      const skus = [
        ...(payload?.product ? [payload.product] : []),
        ...((payload?.elements || []).map(e => e.product).filter(Boolean) as string[]),
      ];
      for (const sku of skus) {
        const blocked = rejectSkuOutsideActiveCatalog(req, sku);
        if (blocked) {
          return blocked;
        }
      }
      const body = addPresidioMockBasketItems(req.body);
      return of(new HttpResponse({ status: 200, body, url: req.url }));
    }

    if (collection && method === 'DELETE') {
      const body = deleteAllPresidioMockBasketItems();
      return of(new HttpResponse({ status: 200, body, url: req.url }));
    }

    // CUSTOMIZATION: multi ship-to — POST items/{id}/separate-lineitem
    if (separate && method === 'POST') {
      const itemId = decodeURIComponent(separate[1]);
      const body = separatePresidioMockBasketItem(itemId, req.body);
      if (!body) {
        return of(
          new HttpResponse({
            status: 404,
            body: {
              errors: [
                {
                  message: `Unknown basket line ${itemId}, or missing shipToAddress for a non-split update`,
                },
              ],
            },
            url: req.url,
          })
        );
      }
      return of(new HttpResponse({ status: 200, body, url: req.url }));
    }

    if (single && method === 'PATCH') {
      const itemId = decodeURIComponent(single[1]);
      const body = updatePresidioMockBasketItem(itemId, req.body);
      if (!body) {
        return of(
          new HttpResponse({
            status: 404,
            body: { errors: [{ message: `Unknown basket line ${itemId}` }] },
            url: req.url,
          })
        );
      }
      return of(new HttpResponse({ status: 200, body, url: req.url }));
    }

    if (single && method === 'DELETE') {
      const itemId = decodeURIComponent(single[1]);
      const body = deletePresidioMockBasketItem(itemId);
      return of(new HttpResponse({ status: 200, body, url: req.url }));
    }

    return null;
  }

  /**
   * DEMO-ONLY: POST baskets/{id}/quotes { id } — merge known quote lines into session basket.
   * Static post.json returned empty infos and never mutated lines, so Add quote to cart looked broken.
   */
  private handleBasketQuoteMutation(
    req: HttpRequest<unknown>,
    restPath: string
  ): Observable<HttpEvent<unknown>> | null {
    const collection = /^baskets\/(?:current|MOCK-BASKET-001)\/quotes$/.exec(restPath);
    const single = /^baskets\/(?:current|MOCK-BASKET-001)\/quotes\/([^/]+)$/.exec(restPath);
    const method = req.method.toUpperCase();

    if (collection && method === 'POST') {
      const payload = this.readJsonBody<{ id?: string }>(req.body);
      const quoteId = (payload?.id || '').trim();
      const result = addPresidioMockQuoteToBasket(quoteId);
      if (result === 'added') {
        return of(new HttpResponse({ status: 200, body: { infos: [] }, url: req.url }));
      }
      // Idempotent: quote already on cart — succeed so DIAS loadBasket continues (no HTTP error).
      if (result === 'already_in_cart') {
        return of(
          new HttpResponse({
            status: 200,
            body: {
              infos: [
                {
                  code: 'quote.already_in_basket.info',
                  message: 'This quote is already in your cart.',
                },
              ],
            },
            url: req.url,
          })
        );
      }
      const status = result === 'unknown' ? 404 : 422;
      const message =
        result === 'not_eligible'
          ? `Quote ${quoteId} cannot be added to the cart`
          : `Unknown quote ${quoteId || '(missing)'}`;
      return throwError(
        () =>
          new HttpErrorResponse({
            status,
            error: {
              errors: [{ code: result, message }],
            },
            url: req.url,
          })
      );
    }

    if (single && method === 'DELETE') {
      // Demo: leave basket lines; production ICM removes quote-originated lines.
      return of(new HttpResponse({ status: 200, body: { infos: [] }, url: req.url }));
    }

    return null;
  }

  /**
   * DEMO-ONLY: create/update/delete basket attributes without per-attribute mock files.
   * Persists attributes in sessionStorage so subsequent basket GET includes them.
   */
  private handleBasketAttributeMutation(
    req: HttpRequest<unknown>,
    restPath: string
  ): Observable<HttpEvent<unknown>> | null {
    const match = /^baskets\/(?:current|MOCK-BASKET-001)\/attributes(?:\/([^/]+))?$/.exec(restPath);
    if (!match) {
      return null;
    }
    const method = req.method.toUpperCase();
    const pathName = match[1] ? decodeURIComponent(match[1]) : undefined;

    if (method === 'POST' || method === 'PATCH') {
      const payload = this.readJsonBody<{ name?: string; type?: string; value?: unknown }>(req.body) || {};
      const attribute = {
        name: payload.name || pathName || 'attribute',
        type: payload.type || 'String',
        value: payload.value ?? '',
      };
      this.upsertBasketAttribute(attribute);
      return of(new HttpResponse({ status: 200, body: attribute, url: req.url }));
    }

    if (method === 'DELETE' && pathName) {
      this.removeBasketAttribute(pathName);
      return of(new HttpResponse({ status: 204, body: null, url: req.url }));
    }

    return null;
  }

  private readBasketAttributes(): Array<{ name: string; type?: string; value: unknown }> {
    if (typeof sessionStorage === 'undefined') {
      return [];
    }
    try {
      const raw = sessionStorage.getItem(MOCK_BASKET_ATTRIBUTES_KEY);
      return raw ? (JSON.parse(raw) as Array<{ name: string; type?: string; value: unknown }>) : [];
    } catch {
      return [];
    }
  }

  private writeBasketAttributes(attrs: Array<{ name: string; type?: string; value: unknown }>): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    sessionStorage.setItem(MOCK_BASKET_ATTRIBUTES_KEY, JSON.stringify(attrs));
  }

  private upsertBasketAttribute(attribute: { name: string; type?: string; value: unknown }): void {
    const attrs = this.readBasketAttributes().filter(a => a.name !== attribute.name);
    attrs.push(attribute);
    this.writeBasketAttributes(attrs);
  }

  private removeBasketAttribute(name: string): void {
    this.writeBasketAttributes(this.readBasketAttributes().filter(a => a.name !== name));
  }

  private applyBasketSessionOverrides(restPath: string, method: string, body: unknown): unknown {
    if (typeof sessionStorage === 'undefined' || !body || typeof body !== 'object') {
      return body;
    }
    const upper = method.toUpperCase();
    const isBasketPayload =
      (upper === 'GET' || upper === 'PATCH') &&
      (restPath === 'baskets/current' || restPath === 'baskets/MOCK-BASKET-001');
    const isValidation =
      upper === 'POST' &&
      (restPath === 'baskets/current/validations' || restPath === 'baskets/MOCK-BASKET-001/validations');
    // DEMO: order create + confirmation GET must mirror checkout session selections.
    const isOrderCreate = upper === 'POST' && (restPath === 'orders' || restPath.startsWith('orders?'));
    const isOrderGet = upper === 'GET' && /^orders\/MOCK-ORD-(NEW|001)$/.test(restPath);
    if (!isBasketPayload && !isValidation && !isOrderCreate && !isOrderGet) {
      return body;
    }

    if (isOrderCreate) {
      // Stamp confirmation from current checkout session, then empty the cart so
      // mini-cart / cart do not keep prior lines or checkout selections.
      const stamped = stampActiveBuyerOntoOrderPayload(this.applyCheckoutSelectionsToOrder(body));
      this.ensureOrderDemoAttachments(stamped);
      const orderNo =
        stamped && typeof stamped === 'object'
          ? String((stamped as { data?: { documentNo?: string; id?: string } }).data?.documentNo ||
              (stamped as { data?: { id?: string } }).data?.id ||
              '')
          : '';
      // DEMO inbound quotes: checkout of a quote-origin basket marks that quote Processing.
      markActivePresidioQuotePurchasedOnCheckout(orderNo || undefined);
      this.persistLastOrderSnapshot(stamped);
      this.clearCheckoutBasketSessionAfterOrder();
      return stamped;
    }

    if (isOrderGet) {
      const snapshot = this.readLastOrderSnapshot();
      const requestedId = restPath.split('/')[1];
      const snapshotId =
        snapshot && typeof snapshot === 'object'
          ? String((snapshot as { data?: { id?: string } }).data?.id || '')
          : '';
      // Only return the confirmation snapshot for the order it belongs to — never for
      // historical MOCK-ORD-001 / other ids (that left LineItemData mapping broken).
      if (snapshot && snapshotId && snapshotId === requestedId) {
        const snap = snapshot as {
          data?: { lineItems?: unknown[] };
          included?: { lineItems?: Record<string, unknown> };
        };
        const snapLineRefs = Array.isArray(snap.data?.lineItems) ? snap.data.lineItems.length : 0;
        const snapIncluded = snap.included?.lineItems ? Object.keys(snap.included.lineItems).length : 0;
        // Prefer fixture when a stale empty confirmation snapshot would hide demo lines.
        if (snapLineRefs > 0 || snapIncluded > 0 || requestedId !== 'MOCK-ORD-NEW') {
          return snapshot;
        }
      }
      // Confirmation order (MOCK-ORD-NEW) may still need checkout session stamp before
      // snapshot exists. Never stamp an empty post-checkout cart onto the fixture —
      // that wiped lineItems on order-history reopen (Reorder / detail looked empty).
      if (requestedId === 'MOCK-ORD-NEW') {
        return stampActiveBuyerOntoOrderPayload(this.applyCheckoutSelectionsToOrder(body));
      }
      return body;
    }

    const invoiceId = sessionStorage.getItem(MOCK_BASKET_INVOICE_ADDR_KEY);
    const shippingId = sessionStorage.getItem(MOCK_BASKET_SHIPPING_ADDR_KEY);
    const paymentInstrument = sessionStorage.getItem(MOCK_BASKET_PAYMENT_KEY);
    const shippingMethodId = sessionStorage.getItem(MOCK_BASKET_SHIPPING_METHOD_KEY);
    const attributes = this.readBasketAttributes();
    const hasAddressOverride = !!(invoiceId || shippingId);
    const hasPaymentOverride = paymentInstrument !== null;
    const hasShippingMethodOverride = !!shippingMethodId;
    const hasAttributesOverride = attributes.length > 0;
    const hasLineOverride = hasPresidioMockBasketLines();
    const recurrenceRaw = sessionStorage.getItem(MOCK_BASKET_RECURRENCE_KEY);
    const hasRecurrenceOverride = recurrenceRaw !== null;
    if (
      !hasAddressOverride &&
      !hasPaymentOverride &&
      !hasShippingMethodOverride &&
      !hasAttributesOverride &&
      !hasLineOverride &&
      !hasRecurrenceOverride
    ) {
      // DEMO: strip fixture preselection so first checkout visit starts empty
      // unless My Account preferred values are applied by the UI.
      return this.stripFixtureCheckoutSelections(body);
    }

    let clone = JSON.parse(JSON.stringify(body)) as {
      data?: Record<string, unknown>;
      included?: Record<string, unknown>;
    };
    // Start from a clean selection surface, then re-apply session overrides.
    clone = this.stripFixtureCheckoutSelections(clone) as typeof clone;

    if (isBasketPayload) {
      if (hasLineOverride) {
        clone = applyPresidioMockBasketLinesToPayload(clone) as typeof clone;
      }
      if (hasAddressOverride) {
        this.writeAddressOntoBasket(clone, invoiceId, shippingId);
      }
      if (hasPaymentOverride) {
        this.writePaymentOntoBasket(clone, paymentInstrument, false);
      }
      if (hasShippingMethodOverride) {
        this.writeShippingMethodOntoBasket(clone, shippingMethodId, false);
      }
      if (hasAttributesOverride) {
        this.writeAttributesOntoBasket(clone, attributes);
      }
      if (hasRecurrenceOverride) {
        this.writeRecurrenceOntoBasket(clone, recurrenceRaw);
      }
      this.finalizeBasketEstimateTotals(clone.data);
      return clone;
    }

    const included = clone.included ?? {};
    const basketMap = included.basket as Record<string, Record<string, unknown>> | undefined;
    const basket = basketMap?.['MOCK-BASKET-001'];
    if (basket) {
      if (hasLineOverride) {
        const applied = applyPresidioMockBasketLinesToPayload(
          { data: basket, included: { lineItems: included.basket_lineItems } },
          { lineItemsIncludeKey: 'lineItems' }
        ) as { data?: Record<string, unknown>; included?: Record<string, unknown> };
        if (applied.data) {
          Object.assign(basket, applied.data);
        }
        if (applied.included?.lineItems) {
          included.basket_lineItems = applied.included.lineItems;
        }
      }
      if (hasAddressOverride) {
        this.writeAddressOntoBasket({ data: basket, included }, invoiceId, shippingId, true);
      }
      if (hasPaymentOverride) {
        this.writePaymentOntoBasket({ data: basket, included }, paymentInstrument, true);
      }
      if (hasShippingMethodOverride) {
        this.writeShippingMethodOntoBasket({ data: basket, included }, shippingMethodId, true);
      }
      if (hasAttributesOverride) {
        this.writeAttributesOntoBasket({ data: basket }, attributes);
      }
      if (hasRecurrenceOverride) {
        this.writeRecurrenceOntoBasket({ data: basket }, recurrenceRaw);
      }
      this.finalizeBasketEstimateTotals(basket);
    }
    return clone;
  }

  /**
   * DEMO: after a successful place-order, empty mutable cart lines and checkout
   * selections so mini-cart / cart / next OPC start clean (NgRx already resets).
   */
  private clearCheckoutBasketSessionAfterOrder(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    clearPresidioMockBasketLines();
    sessionStorage.removeItem(MOCK_BASKET_INVOICE_ADDR_KEY);
    sessionStorage.removeItem(MOCK_BASKET_SHIPPING_ADDR_KEY);
    sessionStorage.removeItem(MOCK_BASKET_PAYMENT_KEY);
    sessionStorage.removeItem(MOCK_BASKET_SHIPPING_METHOD_KEY);
    sessionStorage.removeItem(MOCK_BASKET_RECURRENCE_KEY);
    sessionStorage.removeItem(MOCK_BASKET_ATTRIBUTES_KEY);
  }

  private persistLastOrderSnapshot(body: unknown): void {
    if (typeof sessionStorage === 'undefined' || body == null) {
      return;
    }
    try {
      sessionStorage.setItem(MOCK_LAST_ORDER_KEY, JSON.stringify(body));
    } catch {
      // ignore quota / serialization errors in demo
    }
  }

  private readLastOrderSnapshot(): unknown | null {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }
    const raw = sessionStorage.getItem(MOCK_LAST_ORDER_KEY);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as unknown;
    } catch {
      return null;
    }
  }

  /**
   * DEMO: stamp checkout session selections onto the confirmation order payload
   * (ship-to, invoice, payment, shipping method, lines, attributes, recurrence).
   */
  private applyCheckoutSelectionsToOrder(body: unknown): unknown {
    const invoiceId = sessionStorage.getItem(MOCK_BASKET_INVOICE_ADDR_KEY);
    const shippingId = sessionStorage.getItem(MOCK_BASKET_SHIPPING_ADDR_KEY);
    const paymentInstrument = sessionStorage.getItem(MOCK_BASKET_PAYMENT_KEY);
    const shippingMethodId = sessionStorage.getItem(MOCK_BASKET_SHIPPING_METHOD_KEY);
    const attributes = this.readBasketAttributes();
    const hasAddressOverride = !!(invoiceId || shippingId);
    const hasPaymentOverride = !!paymentInstrument;
    const hasShippingMethodOverride = !!shippingMethodId;
    const hasAttributesOverride = attributes.length > 0;
    // Empty `[]` after place-order still sets the session key — do not treat that as a
    // line override or MOCK-ORD-NEW / confirmation fixtures lose all line items.
    const cartLines = hasPresidioMockBasketLines() ? readPresidioMockBasketLines() : [];
    const hasLineOverride = cartLines.length > 0;
    const recurrenceRaw = sessionStorage.getItem(MOCK_BASKET_RECURRENCE_KEY);
    const hasRecurrenceOverride = recurrenceRaw !== null;

    if (
      !hasAddressOverride &&
      !hasPaymentOverride &&
      !hasShippingMethodOverride &&
      !hasAttributesOverride &&
      !hasLineOverride &&
      !hasRecurrenceOverride
    ) {
      return body;
    }

    let clone = JSON.parse(JSON.stringify(body)) as {
      data?: Record<string, unknown>;
      included?: Record<string, unknown>;
    };

    if (hasLineOverride) {
      clone = applyPresidioMockBasketLinesToPayload(clone) as typeof clone;
    }
    if (hasAddressOverride) {
      this.writeAddressOntoBasket(clone, invoiceId, shippingId, false);
    }
    if (hasPaymentOverride) {
      this.writePaymentOntoBasket(clone, paymentInstrument, false);
      this.enrichOrderPaymentDisplay(clone, paymentInstrument);
    }
    if (hasShippingMethodOverride) {
      this.writeShippingMethodOntoBasket(clone, shippingMethodId, false);
    }
    if (hasAttributesOverride) {
      this.writeAttributesOntoBasket(clone, attributes);
      // Surface PO / order reference on the confirmation document number line.
      const orderRef = attributes.find(a => a.name === 'orderReference' || a.name === 'externalOrderReference');
      if (orderRef?.value != null && clone.data) {
        clone.data.externalOrderReference = String(orderRef.value);
      }
    }
    if (hasRecurrenceOverride) {
      this.writeRecurrenceOntoBasket(clone, recurrenceRaw);
    }
    this.finalizeBasketEstimateTotals(clone.data);
    this.syncOrderBucketsWithCheckoutSelections(clone);
    return stampActiveBuyerOntoOrderPayload(clone);
  }

  /**
   * DEMO: MVP 4.2.7 / FR10.7.3 — ensure new orders have downloadable attachments
   * (order confirmation + PO file when present).
   */
  private ensureOrderDemoAttachments(payload: unknown): void {
    if (!payload || typeof payload !== 'object') {
      return;
    }
    const data = (payload as { data?: Record<string, unknown> }).data;
    if (!data) {
      return;
    }
    const existing = Array.isArray(data.attachments) ? (data.attachments as unknown[]) : [];
    if (existing.length) {
      return;
    }
    const orderId = String(data.id || 'MOCK-ORD-NEW');
    const po = String(data.externalOrderReference || 'PO').trim() || 'PO';
    const attrs = this.readBasketAttributes();
    const poFileAttr = attrs.find(
      a => a.name === 'poFileName' || a.name === 'purchaseOrderFile' || a.name === 'poFile'
    );
    const poFileName = poFileAttr?.value != null ? String(poFileAttr.value) : `${po}.pdf`;
    data.attachments = [
      {
        id: `att-${orderId}-confirm`,
        name: 'Order confirmation.pdf',
        url: '',
        mimeType: 'application/pdf',
      },
      {
        id: `att-${orderId}-po`,
        name: poFileName,
        url: '',
        mimeType: 'application/pdf',
      },
    ];
  }

  /** DEMO: payment.displayName for receipt comes from the method catalog. */
  private enrichOrderPaymentDisplay(
    payload: { data?: Record<string, unknown>; included?: Record<string, unknown> },
    paymentInstrument: string
  ): void {
    const included = payload.included ?? {};
    const payments = included.payments as Record<string, Record<string, unknown>> | undefined;
    const payment = payments?.['open-tender'];
    if (!payment) {
      return;
    }
    const methodId = String(payment.paymentMethod || MOCK_PAYMENT_INSTRUMENT_TO_METHOD[paymentInstrument] || '');
    const catalog = MOCK_PAYMENT_METHOD_CATALOG[methodId];
    if (catalog?.displayName) {
      payment.displayName = catalog.displayName;
    }
    // Prefer richer instrument label (e.g. Visa ···· 1111) for the confirmation line.
    const instruments = included.payments_paymentInstrument as Record<string, Record<string, unknown>> | undefined;
    const instrument = instruments?.[paymentInstrument];
    if (instrument) {
      instrument.accountIdentifier =
        MOCK_PAYMENT_INSTRUMENT_LABELS[paymentInstrument] ||
        instrument.accountIdentifier ||
        paymentInstrument;
    }
  }

  /** DEMO: keep order buckets aligned with common ship-to / shipping method / line IDs. */
  private syncOrderBucketsWithCheckoutSelections(payload: {
    data?: Record<string, unknown>;
    included?: Record<string, unknown>;
  }): void {
    const data = payload.data;
    const included = payload.included;
    if (!data || !included) {
      return;
    }
    const shipUrn = data.commonShipToAddress as string | undefined;
    const methodId = data.commonShippingMethod as string | undefined;
    const lineItemIds = Array.isArray(data.lineItems) ? (data.lineItems as string[]) : [];
    const includedLines = (included.lineItems || {}) as Record<string, unknown>;
    const knownLineIds = lineItemIds.filter(id => !!includedLines[id]);
    // Keep data.lineItems aligned with included so OrderMapper lineItems map cannot throw.
    if (knownLineIds.length !== lineItemIds.length) {
      data.lineItems = knownLineIds.length
        ? knownLineIds
        : Object.keys(includedLines);
    }
    const buckets = included.buckets as Record<string, Record<string, unknown>> | undefined;
    if (buckets) {
      const bucketEntries = Object.values(buckets);
      // Single-bucket demo: all stamped line IDs must live on the bucket the mapper walks.
      if (bucketEntries.length === 1) {
        const bucket = bucketEntries[0];
        if (shipUrn) {
          bucket.shipToAddress = shipUrn;
        }
        if (methodId) {
          bucket.shippingMethod = methodId;
        }
        // Prefer IDs that exist in included.lineItems (avoids LineItemMapper throw).
        const existing = Array.isArray(bucket.lineItems) ? (bucket.lineItems as string[]) : [];
        const keptExisting = existing.filter(id => !!includedLines[id]);
        bucket.lineItems = knownLineIds.length ? knownLineIds : keptExisting;
      } else {
        for (const bucket of bucketEntries) {
          if (shipUrn) {
            bucket.shipToAddress = shipUrn;
          }
          if (methodId) {
            bucket.shippingMethod = methodId;
          }
          // Drop stale fixture line IDs that no longer exist in included.lineItems.
          const existing = Array.isArray(bucket.lineItems) ? (bucket.lineItems as string[]) : [];
          const kept = existing.filter(id => !!includedLines[id]);
          bucket.lineItems = kept.length ? kept : knownLineIds;
        }
      }
    }
    if (shipUrn && included.commonShipToAddress) {
      included.buckets_shipToAddress = {
        ...(included.commonShipToAddress as Record<string, unknown>),
      };
    }
    if (methodId && included.commonShippingMethod) {
      included.buckets_shippingMethod = {
        ...(included.commonShippingMethod as Record<string, unknown>),
      };
    }
  }

  private writeRecurrenceOntoBasket(
    payload: { data?: Record<string, unknown> },
    recurrenceRaw: string | null
  ): void {
    if (!recurrenceRaw) {
      return;
    }
    try {
      const recurrence = JSON.parse(recurrenceRaw) as Record<string, unknown>;
      const data = payload.data ?? {};
      data.recurrence = recurrence;
      payload.data = data;
    } catch {
      // ignore corrupt session value
    }
  }

  private writeShippingMethodOntoBasket(
    payload: { data?: Record<string, unknown>; included?: Record<string, unknown> },
    shippingMethodId: string | null,
    validationIncludes: boolean
  ): void {
    if (!shippingMethodId) {
      return;
    }
    const data = payload.data ?? {};
    const included = payload.included ?? (payload.included = {});
    const method = {
      ...(MOCK_SHIPPING_METHOD_CATALOG[shippingMethodId] || MOCK_STD_SHIPPING_METHOD),
      id: shippingMethodId,
    };
    const shippingCost = method.shippingCosts?.gross?.value ?? MOCK_STD_SHIPPING_COST;
    data.commonShippingMethod = shippingMethodId;
    if (validationIncludes) {
      included.basket_commonShippingMethod = { [shippingMethodId]: method };
    } else {
      included.commonShippingMethod = { [shippingMethodId]: method };
    }
    this.applyShippingCostsToBasketTotals(data, shippingCost);
    payload.data = data;
    payload.included = included;
  }

  /**
   * DEMO: remove fixture-seeded ship-to / payment / shipping method so first checkout
   * entry is empty unless session overrides or preferred defaults are applied.
   */
  private stripFixtureCheckoutSelections(body: unknown): unknown {
    if (!body || typeof body !== 'object') {
      return body;
    }
    const clone = JSON.parse(JSON.stringify(body)) as {
      data?: Record<string, unknown>;
      included?: Record<string, unknown>;
    };
    const data = clone.data;
    const included = clone.included;
    const stripBasketFields = (basket: Record<string, unknown> | undefined) => {
      if (!basket) {
        return;
      }
      delete basket.commonShipToAddress;
      delete basket.commonShippingMethod;
      delete basket.payments;
      delete basket.invoiceToAddress;
      // Only adjust totals when this object is a real basket (has totals).
      if (basket.totals) {
        this.applyShippingCostsToBasketTotals(basket, 0);
      }
    };
    // Basket GET/PATCH: selections live on data. Validation: data is the envelope (skip).
    if (data && (data.lineItems || data.totals || data.id === 'MOCK-BASKET-001' || data.id === 'current')) {
      stripBasketFields(data);
    }
    if (included) {
      delete included.commonShipToAddress;
      delete included.basket_commonShipToAddress;
      delete included.commonShippingMethod;
      delete included.basket_commonShippingMethod;
      delete included.payments;
      delete included.basket_payments;
      delete included.payments_paymentMethod;
      delete included.basket_payments_paymentMethod;
      delete included.payments_paymentInstrument;
      delete included.basket_payments_paymentInstrument;
      delete included.invoiceToAddress;
      delete included.basket_invoiceToAddress;
      // Validation envelopes nest the basket under included.basket.
      const basketMap = included.basket as Record<string, Record<string, unknown>> | undefined;
      if (basketMap) {
        for (const basket of Object.values(basketMap)) {
          stripBasketFields(basket);
        }
      }
    }
    return clone;
  }

  /**
   * DEMO: ship-to + payment + shipping method must all be present before freight and
   * estimated sales tax are calculated into basket totals (OPC cost summary).
   * Warehouse login skips payment. MVP 2.6.3 — $0 merchandise incurs no freight.
   */
  private basketHasEstimatePrerequisites(data: Record<string, unknown>): boolean {
    const shipTo = data.commonShipToAddress;
    const hasShipTo =
      typeof shipTo === 'string'
        ? !!shipTo.trim()
        : !!(shipTo && typeof shipTo === 'object' && ((shipTo as { id?: string; urn?: string }).id || (shipTo as { urn?: string }).urn));

    const shippingMethod = data.commonShippingMethod;
    const hasShipping =
      typeof shippingMethod === 'string'
        ? !!shippingMethod.trim()
        : !!(shippingMethod && typeof shippingMethod === 'object' && (shippingMethod as { id?: string }).id);

    const payments = data.payments;
    const hasPayment = Array.isArray(payments)
      ? payments.length > 0
      : !!(data.payment && typeof data.payment === 'object');

    const paymentRequired = !isPresidioMockWarehouseLogin();
    return hasShipTo && hasShipping && (!paymentRequired || hasPayment);
  }

  private resolveBasketShippingMethodCost(data: Record<string, unknown>): number {
    const shippingMethod = data.commonShippingMethod;
    const methodId =
      typeof shippingMethod === 'string'
        ? shippingMethod
        : shippingMethod && typeof shippingMethod === 'object'
          ? String((shippingMethod as { id?: string }).id || '')
          : '';
    if (!methodId) {
      return 0;
    }
    const catalog = MOCK_SHIPPING_METHOD_CATALOG[methodId];
    return catalog?.shippingCosts?.gross?.value ?? MOCK_STD_SHIPPING_COST;
  }

  /**
   * DEMO: stamp freight + estimated tax only when estimate prerequisites are met.
   * Otherwise keep merchandise-only totals (no provisional $0 shipping / no tax).
   */
  private applyShippingCostsToBasketTotals(data: Record<string, unknown>, shippingCost: number): void {
    const totals = ((data.totals as Record<string, unknown>) ?? {}) as Record<string, unknown>;
    const itemGross =
      ((totals.itemTotal as { gross?: { value?: number } } | undefined)?.gross?.value ??
        (totals.undiscountedItemTotal as { gross?: { value?: number } } | undefined)?.gross?.value ??
        0) || 0;
    const ready = this.basketHasEstimatePrerequisites(data);
    const catalogCost = shippingCost > 0 ? shippingCost : this.resolveBasketShippingMethodCost(data);
    const effectiveShipping = ready && itemGross > 0 ? catalogCost : 0;
    totals.shippingTotal = mockMoney(effectiveShipping);
    totals.undiscountedShippingTotal = mockMoney(effectiveShipping);
    totals.grandTotal = ready
      ? moneyWithPresidioMockTax(itemGross + effectiveShipping)
      : mockMoney(itemGross);
    data.totals = totals;
  }

  /** DEMO: recompute estimate totals after checkout session selections change. */
  private finalizeBasketEstimateTotals(data: Record<string, unknown> | undefined): void {
    if (!data?.totals) {
      return;
    }
    this.applyShippingCostsToBasketTotals(data, this.resolveBasketShippingMethodCost(data));
  }

  private writeAttributesOntoBasket(
    payload: { data?: Record<string, unknown> },
    attributes: Array<{ name: string; type?: string; value: unknown }>
  ): void {
    const data = payload.data ?? {};
    const existing = Array.isArray(data.attributes)
      ? (data.attributes as Array<{ name: string; type?: string; value: unknown }>)
      : [];
    const byName = new Map(existing.map(a => [a.name, a]));
    for (const attr of attributes) {
      byName.set(attr.name, attr);
    }
    data.attributes = Array.from(byName.values());
    payload.data = data;
  }

  private writePaymentOntoBasket(
    payload: { data?: Record<string, unknown>; included?: Record<string, unknown> },
    paymentInstrument: string | null,
    validationIncludes: boolean
  ): void {
    const data = payload.data ?? {};
    const included = payload.included ?? (payload.included = {});

    if (!paymentInstrument) {
      delete data.payments;
      if (validationIncludes) {
        delete included.basket_payments;
        delete included.basket_payments_paymentMethod;
        delete included.basket_payments_paymentInstrument;
      } else {
        delete included.payments;
        delete included.payments_paymentMethod;
        delete included.payments_paymentInstrument;
      }
      payload.data = data;
      payload.included = included;
      return;
    }

    const methodId = MOCK_PAYMENT_INSTRUMENT_TO_METHOD[paymentInstrument] || paymentInstrument;
    const catalogMethod = MOCK_PAYMENT_METHOD_CATALOG[methodId];
    const payment = {
      ...MOCK_OPEN_TENDER_PAYMENT,
      paymentInstrument,
      paymentMethod: methodId,
    };
    const method = catalogMethod
      ? {
          ...catalogMethod,
          paymentInstruments: [paymentInstrument],
        }
      : {
          ...MOCK_INVOICE_PAYMENT_METHOD,
          id: methodId,
          serviceID: methodId,
          paymentInstruments: [paymentInstrument],
        };
    const instrument = {
      id: paymentInstrument,
      accountIdentifier: MOCK_PAYMENT_INSTRUMENT_LABELS[paymentInstrument] || paymentInstrument,
    };

    data.payments = ['open-tender'];
    if (validationIncludes) {
      included.basket_payments = { 'open-tender': payment };
      included.basket_payments_paymentMethod = { [methodId]: method };
      included.basket_payments_paymentInstrument = { [paymentInstrument]: instrument };
    } else {
      included.payments = { 'open-tender': payment };
      included.payments_paymentMethod = { [methodId]: method };
      included.payments_paymentInstrument = { [paymentInstrument]: instrument };
    }
    payload.data = data;
    payload.included = included;
  }

  private writeAddressOntoBasket(
    payload: { data?: Record<string, unknown>; included?: Record<string, unknown> },
    invoiceId: string | null,
    shippingId: string | null,
    validationIncludes = false
  ): void {
    const data = payload.data ?? {};
    const included = payload.included ?? (payload.included = {});

    const resolveMockAddress = (
      idOrUrn: string
    ): { urn: string; address: Record<string, unknown> } | null => {
      const id = this.resolveMockAddressId(idOrUrn) || idOrUrn;
      if (MOCK_ADDRESS_BY_ID[id]) {
        return MOCK_ADDRESS_BY_ID[id];
      }
      const created = this.readCreatedAddresses().find(
        a => String(a.id) === id || String(a.urn || '') === idOrUrn
      );
      if (!created) {
        return null;
      }
      return {
        urn: String(created.urn || `urn_${id.replace(/-/g, '_')}`),
        address: { ...created },
      };
    };

    if (invoiceId) {
      const resolved = resolveMockAddress(invoiceId);
      if (resolved) {
        const { urn, address } = resolved;
        const contactAddress = applyActiveBuyerContactToAddress({ ...address });
        data.invoiceToAddress = urn;
        // Always include both keys — basket GET maps `invoiceToAddress`, validation maps `basket_*`.
        included.invoiceToAddress = { [urn]: contactAddress };
        if (validationIncludes) {
          included.basket_invoiceToAddress = { [urn]: { ...contactAddress } };
        }
      }
    }
    if (shippingId) {
      const resolved = resolveMockAddress(shippingId);
      if (resolved) {
        const { urn, address } = resolved;
        const contactAddress = applyActiveBuyerContactToAddress({ ...address });
        data.commonShipToAddress = urn;
        included.commonShipToAddress = { [urn]: contactAddress };
        if (validationIncludes) {
          included.basket_commonShipToAddress = { [urn]: { ...contactAddress } };
        }
      }
    }
    payload.data = data;
    payload.included = included;
  }

  private readJsonBody<T>(body: unknown): T | null {
    if (!body) {
      return null;
    }
    if (typeof body === 'string') {
      try {
        return JSON.parse(body) as T;
      } catch {
        return null;
      }
    }
    if (typeof body === 'object') {
      return body as T;
    }
    return null;
  }

  private urlHasToBeMocked(url: string): boolean {
    if (url.startsWith(this.restEndpoint)) {
      const path = this.getRestPath(url);
      return this.matchPath(path, this.apiMockPaths);
    }
    return false;
  }

  private readQueryParam(url: string, key: string): string | null {
    const q = url.includes('?') ? url.substring(url.indexOf('?') + 1) : '';
    if (!q) {
      return null;
    }
    const params = new URLSearchParams(q);
    return params.get(key);
  }

  private getRestPath(url: string): string {
    const withoutApplication = url.substring(this.restEndpoint.length);
    const path = withoutApplication.substring(withoutApplication.indexOf('/') + 1).split('?')[0];
    // ICM places matrix params on the first segment (`categories;spgid=…/HW/products`).
    // Strip them so list filters, productfilters rebuild, and mock path matching see a clean path.
    return path.replace(/;[^/]*/g, '');
  }

  /**
   * DEMO-ONLY: Quick Order / deep links may request products by manufacturer part #.
   * Rewrite `products/{mpn}` (and common sub-resources) to the ICM sku mock folder.
   */
  private resolveProductPathByManufacturerPart(restPath: string): string {
    const match = /^products\/([^/]+)(\/.*)?$/.exec(restPath);
    if (!match) {
      return restPath;
    }
    const entered = decodeURIComponent(match[1]);
    const resolved = resolvePresidioProductSku(entered);
    if (!resolved || resolved === entered.trim().toUpperCase()) {
      // Still normalize case when already an ICM sku
      if (resolved && resolved !== entered) {
        return `products/${resolved}${match[2] || ''}`;
      }
      return restPath;
    }
    return `products/${resolved}${match[2] || ''}`;
  }

  matchPath(requestedPath: string, pathArray: string[] = []) {
    return pathArray.reduce((acc, val) => acc || new RegExp(val).test(requestedPath), false);
  }
}
